<template>
  <HrPageLayout
    title="Payroll ledger vs employee register"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="pcr-content">
      <div class="alert alert-info hr-mb-3">
        <h6 class="alert-heading hr-mb-2">Payroll ledger vs register — how to use</h6>
        <ol class="mb-2 ps-3">
          <li><strong>Month folder / repository:</strong> Pick <strong>Month</strong> and <strong>Year</strong>. Every upload is stored under that period. Re-open the month later and use <strong>Download file</strong> to retrieve the original payroll, pension, and gratuity files (monthly repository).</li>
          <li><strong>Upload inputs:</strong> Payroll ledger + four pension files + gratuity report. NITA amount per employee must be set before analysis.</li>
          <li>
            <span class="pcr-gratuity-blink">
              GRATUITY TOTAL ROW (REQUIRED): IN THE GRATUITY EXCEL/CSV, ADD A BOTTOM TOTAL (OR GRAND TOTAL) ROW AND PUT THE GRAND TOTAL FIGURE IN THE GRATUITY COLUMN. FILES WITHOUT THIS ROW WILL NOT BE ACCEPTED FOR UPLOAD OR ANALYSIS. ANALYSIS USES THAT TOTAL-ROW FIGURE ONLY — IT DOES NOT SUM EVERY LINE AMOUNT.
            </span>
          </li>
          <li><strong>Analyze &amp; save:</strong> Run <strong>Load register &amp; analyze</strong>, then <strong>Save report</strong> so multi-month wage bill and saved-report history are updated.</li>
          <li><strong>Reports (match docus templates):</strong>
            Spending Entity (Costings) Summary, Spending Entities Detailed Report, Wagebill Summary, Multi-month wage bill, Missing from Payroll, Extra from Payroll — each as <strong>CSV</strong> and <strong>PDF with county letterhead</strong>.
          </li>
        </ol>
        <p class="mb-0 hr-small text-muted">Removed as separate report views: standalone Pension, Gratuity, NITA, and the old “Dept detail” label (now Spending Entities Detailed Report). Pension/gratuity/NITA still feed the costing &amp; wage-bill calculations from the uploads above.</p>
      </div>
      <div class="filters-card hr-mb-3">
          <p class="text-muted hr-small hr-mb-2">
            <strong>Spending entities</strong> are derived from <strong>Employee Register departments</strong> only.
            Work station is never forced when an employee has no department. Logos &amp; contacts for PDFs: System Management → Setup &amp; Configuration.
          </p>
        <div class="filters-grid">
          <div class="filter-group">
            <label class="filter-label">Month</label>
            <select v-model.number="periodMonth" class="filter-select" :disabled="periodFinalized" @change="onPeriodChange">
              <option v-for="m in monthOptions" :key="'m-' + m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Year</label>
            <select v-model.number="periodYear" class="filter-select" :disabled="periodFinalized" @change="onPeriodChange">
              <option v-for="y in yearOptions" :key="'y-' + y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Register scope</label>
            <select v-model="registerScope" class="filter-select">
              <option value="active">Active employees only</option>
              <option value="all">All employees (any status)</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">NITA per employee (KES)</label>
            <input v-model.number="nitaPerEmployee" type="number" min="0" step="0.01" class="filter-input" :disabled="periodFinalized || !canManage">
          </div>
          <div class="filter-group align-end">
            <button type="button" class="hr-btn hr-btn-outline-primary hr-btn-sm" :disabled="!canManage || periodFinalized || savingNita" @click="saveNitaConfig">
              {{ savingNita ? "Saving…" : "Save NITA" }}
            </button>
          </div>
        </div>

        <div v-if="periodFinalized" class="alert alert-info hr-mt-2 hr-mb-0">
          This month is <strong>finalized</strong>. Uploads and analysis are locked.
          <button v-if="canReopen" type="button" class="hr-btn hr-btn-warning hr-btn-sm hr-ms-2" @click="reopenMonth">Reopen month</button>
        </div>

        <div class="upload-grid hr-mt-3">
          <div class="upload-block">
            <label class="filter-label">Payroll ledger</label>
            <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus.payroll" @change="stageFile($event, 'payroll')">
            <span v-if="uploadStatus.payroll" class="hr-small text-success">✓ {{ uploadStatus.payroll }}</span>
            <span v-else-if="stagedFiles.payroll" class="hr-small text-muted">Staged: {{ stagedFiles.payroll.name }}</span>
            <button
              v-if="uploadStatus.payroll"
              type="button"
              class="hr-btn hr-btn-outline-primary hr-btn-sm hr-mt-1"
              :disabled="downloadingUpload === 'payroll'"
              @click="downloadOriginalUpload('payroll')"
            >
              {{ downloadingUpload === 'payroll' ? 'Downloading…' : 'Download file' }}
            </button>
            <button
              v-if="uploadStatus.payroll"
              type="button"
              class="btn btn-danger btn-sm hr-mt-1 hr-ms-1"
              style="padding: 4px 10px;"
              :disabled="downloadingUpload === 'payroll'"
              @click="deleteUpload('payroll')"
            >
              Delete
            </button>
          </div>
          <div v-for="code in [16, 32, 33, 101]" :key="'pen-' + code" class="upload-block">
            <label class="filter-label">Pension file (code {{ code }})</label>
            <input type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus['pension_' + code]" @change="stageFile($event, 'pension_' + code)">
            <span v-if="uploadStatus['pension_' + code]" class="hr-small text-success">✓ {{ uploadStatus['pension_' + code] }}</span>
            <span v-else-if="stagedFiles['pension_' + code]" class="hr-small text-muted">Staged: {{ stagedFiles['pension_' + code].name }}</span>
            <button
              v-if="uploadStatus['pension_' + code]"
              type="button"
              class="hr-btn hr-btn-outline-primary hr-btn-sm hr-mt-1"
              :disabled="downloadingUpload === 'pension_' + code"
              @click="downloadOriginalUpload('pension_' + code)"
            >
              {{ downloadingUpload === ('pension_' + code) ? 'Downloading…' : 'Download file' }}
            </button>
            <button
              v-if="uploadStatus['pension_' + code]"
              type="button"
              class="btn btn-danger btn-sm hr-mt-1 hr-ms-1"
              style="padding: 4px 10px;"
              :disabled="downloadingUpload === 'pension_' + code"
              @click="deleteUpload('pension_' + code)"
            >
              Delete
            </button>
          </div>
          <div class="upload-block">
            <label class="filter-label">Gratuity report</label>
            <input type="file" accept=".csv,.xlsx,.xls" class="filter-input" :disabled="periodFinalized || uploadingAll || !!uploadStatus.gratuity" @change="stageFile($event, 'gratuity')">
            <span v-if="uploadStatus.gratuity" class="hr-small text-success">✓ {{ uploadStatus.gratuity }}</span>
            <span v-else-if="stagedFiles.gratuity" class="hr-small text-muted">Staged: {{ stagedFiles.gratuity.name }}</span>
            <p class="pcr-gratuity-blink hr-mt-1 mb-0">
              BEFORE UPLOAD: Keep headers SNO / IDNUM / … / GRATUITY (title rows above are OK). Bottom row must say TOTAL (can be under IDNUM) and put a real number in the GRATUITY cell — not blank or “-”.
            </p>
            <button
              v-if="uploadStatus.gratuity"
              type="button"
              class="hr-btn hr-btn-outline-primary hr-btn-sm hr-mt-1"
              :disabled="downloadingUpload === 'gratuity'"
              @click="downloadOriginalUpload('gratuity')"
            >
              {{ downloadingUpload === 'gratuity' ? 'Downloading…' : 'Download file' }}
            </button>
            <button
              v-if="uploadStatus.gratuity"
              type="button"
              class="btn btn-danger btn-sm hr-mt-1 hr-ms-1"
              style="padding: 4px 10px;"
              :disabled="downloadingUpload === 'gratuity'"
              @click="deleteUpload('gratuity')"
            >
              Delete
            </button>
          </div>
        </div>

        <div v-if="analysisDone && gratuityRecon" class="alert hr-mt-2 hr-mb-0" :class="gratuityRecon.totalRowFound ? 'alert-success' : 'alert-danger'">
          <template v-if="gratuityRecon.totalRowFound">
            Gratuity wage-bill figure: <strong>{{ formatMoney(gratuityRecon.uploadedTotal) }}</strong>
            taken from <strong>TOTAL row</strong>
            <span v-if="gratuityRecon.amountColumn"> (column: {{ gratuityRecon.amountColumn }})</span>.
          </template>
          <template v-else>
            <span class="pcr-gratuity-blink">
              GRATUITY FILE HAS NO TOTAL / GRAND TOTAL ROW. ADD A TOTAL ROW WITH THE FIGURE IN THE GRATUITY COLUMN AND RE-UPLOAD. ANALYSIS IS BLOCKED.
            </span>
          </template>
        </div>

        <div v-if="Object.keys(stagedFiles).length > 0" class="hr-mt-3">
          <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" :disabled="uploadingAll" @click="uploadAllStagedFiles">
            {{ uploadingAll ? 'Uploading...' : 'Initiate Upload of Staged Files' }}
          </button>
          
          <div v-if="uploadingAll || uploadProgress > 0" class="progress hr-mt-2" style="height: 10px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100"></div>
          </div>
        </div>
        
        <div v-if="uploadErrors && uploadErrors.length > 0" class="alert alert-danger hr-mt-2">
          <strong>Upload errors:</strong>
          <ul class="mb-0">
            <li v-for="(err, idx) in uploadErrors" :key="idx">{{ err }}</li>
          </ul>
        </div>

        <div v-if="preValidation" class="validation-panel hr-mt-3">
          <h6 class="hr-mb-2">Pre-analysis checklist</h6>
          <ul class="validation-list">
            <li :class="preValidation.checks.payroll ? 'ok' : 'fail'">Payroll file uploaded</li>
            <li :class="preValidation.checks.register ? 'ok' : 'fail'">Employee register available</li>
            <li :class="pensionFilesReady ? 'ok' : 'fail'">Four pension files uploaded</li>
            <li :class="preValidation.checks.gratuity ? 'ok' : 'fail'">Gratuity file uploaded</li>
            <li :class="gratuityTotalRowReady ? 'ok' : 'fail'">Gratuity TOTAL / GRAND TOTAL row present</li>
            <li :class="preValidation.checks.nita ? 'ok' : 'fail'">NITA amount configured</li>
          </ul>
          <p v-if="preValidation.errors.length" class="text-danger hr-small">{{ preValidation.errors.join(" ") }}</p>
        </div>

        <div class="d-flex flex-wrap gap-2 hr-mt-3">
          <button type="button" class="hr-btn hr-btn-primary" :disabled="loading || !canRunAnalysis" @click="runAnalysis">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            Load register &amp; analyze
          </button>
          <button type="button" class="hr-btn hr-btn-outline-secondary" :disabled="!analysisDone" @click="clearAll">Clear</button>
          <button type="button" class="hr-btn hr-btn-success" :disabled="!analysisDone || savingReport" @click="openSaveReport">
            {{ savingReport ? "Saving…" : "Save report" }}
          </button>
          <button v-if="canFinalize && analysisDone && !periodFinalized" type="button" class="hr-btn hr-btn-warning" @click="finalizeMonth">Finalize month</button>
          <button v-if="analysisDone" type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportPdfSummary">Costings PDF</button>
          <div v-if="analysisDone" class="dropdown d-inline-block">
            <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm dropdown-toggle" data-bs-toggle="dropdown">
              PDF reports (letterhead)
            </button>
            <ul class="dropdown-menu">
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('spending')">Spending Entity (Costings) Summary</button></li>
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('spendingDetail')">Spending Entities Detailed Report</button></li>
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('wagebill')">Wagebill Summary</button></li>
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('wagebillMulti')">Multi-month wage bill</button></li>
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('registerOnly')">Missing from Payroll</button></li>
              <li><button type="button" class="dropdown-item" @click="exportReportPdf('csvOnly')">Extra from Payroll</button></li>
            </ul>
          </div>
          <div v-if="analysisDone" class="dropdown d-inline-block">
            <button type="button" class="hr-btn hr-btn-outline-primary hr-btn-sm dropdown-toggle" data-bs-toggle="dropdown">
              CSV reports
            </button>
            <ul class="dropdown-menu">
              <li><button type="button" class="dropdown-item" @click="exportTableCsv('spending')">Spending Entity (Costings) Summary</button></li>
              <li><button type="button" class="dropdown-item" @click="exportTableCsv('spendingDetail')">Spending Entities Detailed Report</button></li>
              <li><button type="button" class="dropdown-item" @click="exportTableCsv('wagebill')">Wagebill Summary</button></li>
              <li><button type="button" class="dropdown-item" @click="exportMultiMonthWageCsv">Multi-month wage bill</button></li>
              <li><button type="button" class="dropdown-item" @click="exportTableCsv('registerOnly')">Missing from Payroll</button></li>
              <li><button type="button" class="dropdown-item" @click="exportTableCsv('csvOnly')">Extra from Payroll</button></li>
            </ul>
          </div>
          <button v-if="analysisDone" type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportExcelWorkbook">Export Excel workbook</button>
        </div>

        <div v-if="headers.length" v-show="false" class="hr-mt-3 pcr-mapping">
          <h6 class="hr-mb-2">Column mapping (payroll ledger)</h6>
          <div class="mapping-grid">
            <div class="mapping-row">
              <span>Staff / ID</span>
              <select v-model="mapId" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'id-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Department (CSV — for mismatch checks)</span>
              <select v-model="mapDept" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'dept-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Pay amount</span>
              <select v-model="mapPay" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'pay-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="mapping-row">
              <span>Name (optional)</span>
              <select v-model="mapName" class="filter-select">
                <option value="">— Auto —</option>
                <option v-for="h in headers" :key="'nm-' + h" :value="h">{{ h }}</option>
              </select>
            </div>
          </div>
          <p v-if="detectedEarnings.length || detectedDeductions.length" class="hr-small text-muted hr-mt-2">
            Detected {{ detectedEarnings.length }} earning code(s), {{ detectedDeductions.length }} deduction code(s) — saved to template catalog on analyze.
          </p>
        </div>
      </div>

      <div v-if="errorMsg" class="alert alert-warning">{{ errorMsg }}</div>

      <div v-if="saveReportMessage" class="alert" :class="saveReportError ? 'alert-danger' : 'alert-success'">{{ saveReportMessage }}</div>
      <div v-if="analysisDone && activeTab !== 'saved'" class="hr-mb-2">
        <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm" @click="activeTab = 'saved'">
          Back to saved reports
        </button>
      </div>

      <ul class="nav nav-tabs hr-mb-3">
        <li v-for="t in tabs" :key="t.id" class="nav-item">
          <button
            type="button"
            class="nav-link"
            :class="{ active: activeTab === t.id, disabled: t.requiresAnalysis && !analysisDone }"
            :disabled="t.requiresAnalysis && !analysisDone"
            @click="onTabClick(t.id)"
          >
            {{ t.label }}
          </button>
        </li>
      </ul>

      <div v-show="activeTab === 'saved'" class="table-card hr-mb-3">
        <p class="text-muted hr-small">
          Saved reconciliation snapshots (summary, spending entities, wage bill, matched / missing lists). Run <strong>Save report</strong> after analysis to store a copy.
        </p>
        <button
          type="button"
          class="hr-btn hr-btn-outline-secondary hr-btn-sm hr-mb-2"
          :disabled="savedReportsLoading"
          @click="loadSavedReports"
        >
          Refresh list
        </button>
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Saved at</th>
                <th>Period</th>
                <th>Title</th>
                <th>Employees</th>
                <th>Gross payroll</th>
                <th>Variance</th>
                <th>Register scope</th>
                <th>Saved by</th>
                <th style="width: 200px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="savedReportsLoading">
                <td colspan="9" class="text-muted">Loading…</td>
              </tr>
              <tr v-else-if="!savedReports.length">
                <td colspan="9" class="text-muted">No saved reports yet.</td>
              </tr>
              <tr v-for="r in savedReports" :key="'sr-' + r.id">
                <td>{{ r.created_at }}</td>
                <td>{{ formatReportPeriod(r) }}</td>
                <td>{{ r.title }}</td>
                <td>{{ r.employee_count != null ? r.employee_count : "—" }}</td>
                <td>{{ r.gross_payroll != null ? formatMoney(r.gross_payroll) : "—" }}</td>
                <td>{{ r.variance_total != null ? formatMoney(r.variance_total) : "—" }}</td>
                <td>{{ r.register_scope }}</td>
                <td>{{ r.created_by_username || "—" }}</td>
                <td>
                  <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" @click="loadSavedReport(r.id)">Load</button>
                  <button type="button" class="hr-btn hr-btn-outline-danger hr-btn-sm hr-ms-1" @click="deleteSavedReport(r.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-show="activeTab === 'repository'" class="table-card hr-mb-3">
        <h6 class="hr-mb-2">Monthly file repository — {{ periodLabel }}</h6>
        <p class="text-muted hr-small">
          Original uploads for this month/year are kept on the server (payroll ledger, pension files, gratuity).
          They remain available after analysis for audit and re-download — like a document repository per month.
        </p>
        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>File type</th>
                <th>Status</th>
                <th style="width: 160px">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Payroll ledger</td>
                <td>{{ uploadStatus.payroll || 'Not uploaded for this month' }}</td>
                <td>
                  <button
                    type="button"
                    class="hr-btn hr-btn-outline-primary hr-btn-sm"
                    :disabled="!uploadStatus.payroll || downloadingUpload === 'payroll'"
                    @click="downloadOriginalUpload('payroll')"
                  >Download</button>
                </td>
              </tr>
              <tr v-for="code in [16, 32, 33, 101]" :key="'repo-pen-' + code">
                <td>Pension file (code {{ code }})</td>
                <td>{{ uploadStatus['pension_' + code] || 'Not uploaded for this month' }}</td>
                <td>
                  <button
                    type="button"
                    class="hr-btn hr-btn-outline-primary hr-btn-sm"
                    :disabled="!uploadStatus['pension_' + code] || downloadingUpload === ('pension_' + code)"
                    @click="downloadOriginalUpload('pension_' + code)"
                  >Download</button>
                </td>
              </tr>
              <tr>
                <td>Gratuity report</td>
                <td>{{ uploadStatus.gratuity || 'Not uploaded for this month' }}</td>
                <td>
                  <button
                    type="button"
                    class="hr-btn hr-btn-outline-primary hr-btn-sm"
                    :disabled="!uploadStatus.gratuity || downloadingUpload === 'gratuity'"
                    @click="downloadOriginalUpload('gratuity')"
                  >Download</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-show="activeTab === 'wagebillMulti'" class="table-card hr-mb-3">
        <p class="text-muted hr-small">
          <strong>Multi-month wage bill ({{ multiMonthWage.year || periodYear }})</strong> —
          lightweight monthly snapshots. Month 1 appears as first cost column; each later upload/save adds a column.
        </p>
        <div class="d-flex flex-wrap gap-2 hr-mb-2">
          <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm" :disabled="multiMonthWage.loading" @click="loadMultiMonthWageBill">
            {{ multiMonthWage.loading ? 'Loading…' : 'Refresh' }}
          </button>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm" :disabled="!multiMonthWage.entities.length" @click="exportMultiMonthWageCsv">Download CSV</button>
          <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" :disabled="!multiMonthWage.entities.length" @click="exportReportPdf('wagebillMulti')">PDF (letterhead)</button>
        </div>
        <div class="table-responsive" style="max-height: 520px; overflow: auto;">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Spending entity</th>
                <th v-for="c in multiMonthColumns" :key="'mmh-' + c.value">{{ c.label }} {{ multiMonthWage.year }}</th>
                <th>YTD total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="multiMonthWage.loading">
                <td :colspan="2 + multiMonthColumns.length" class="text-muted">Loading multi-month data…</td>
              </tr>
              <tr v-else-if="!multiMonthWage.entities.length">
                <td :colspan="2 + multiMonthColumns.length" class="text-muted">
                  No monthly snapshots yet. Run analysis and <strong>Save report</strong> for each month to add columns.
                </td>
              </tr>
              <tr v-for="(e, i) in multiMonthWage.entities" :key="'mme-' + i">
                <td>{{ e.entity }}</td>
                <td v-for="c in multiMonthColumns" :key="'mmc-' + e.entity + '-' + c.value">
                  {{ formatMoney((e.months && e.months[c.value] && e.months[c.value].total) || 0) }}
                </td>
                <td class="fw-bold">{{ formatMoney(entityYtd(e)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template v-if="analysisDone">

        <div v-show="false" class="table-card">
          <!-- legacy summary KPIs retained internally only; not shown in nav tabs -->
          <div class="d-flex flex-wrap gap-2 hr-mb-3">
            <button type="button" class="hr-btn hr-btn-success hr-btn-sm" @click="exportSummaryCsv">Download summary CSV</button>
            <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportReportPdf('summary')">PDF (letterhead)</button>
          </div>
          <div class="row g-3 hr-mb-3">
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.csvRowCount }}</div>
                <div class="pcr-stat-label">Rows in CSV</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ summary.registerCount }}</div>
                <div class="pcr-stat-label">Employees in register (scope)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-success">{{ summary.matchedCount }}</div>
                <div class="pcr-stat-label">Matched (CSV ↔ register)</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-warning">{{ summary.csvOnlyCount }}</div>
                <div class="pcr-stat-label">In CSV, not in register</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-danger">{{ summary.registerOnlyCount }}</div>
                <div class="pcr-stat-label">Missing from payroll upload</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value">{{ formatMoney(summary.grossPay || summary.totalCsvPay) }}</div>
                <div class="pcr-stat-label">Gross payroll</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-warning">{{ summary.duplicateCount || 0 }}</div>
                <div class="pcr-stat-label">Duplicate payroll IDs</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="pcr-stat">
                <div class="pcr-stat-value text-danger">{{ summary.inactivePaidCount || 0 }}</div>
                <div class="pcr-stat-label">Inactive but paid</div>
              </div>
            </div>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <h6>Spending Entities Summary Report — {{ periodLabel }}</h6>
          <div class="row g-3 hr-mb-3" v-if="executiveSummary">
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalPayrollCost) }}</div><div class="pcr-stat-label">Total payroll (gross)</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalBasicPay || spendingTotals.basic) }}</div><div class="pcr-stat-label">Total basic pay</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalPensionCost) }}</div><div class="pcr-stat-label">Total pension (902)</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalGratuityCost) }}</div><div class="pcr-stat-label">Total gratuity</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ formatMoney(executiveSummary.totalNitaCost) }}</div><div class="pcr-stat-label">Expected NITA</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value">{{ executiveSummary.employeeCount }}</div><div class="pcr-stat-label">Employees matched</div></div></div>
            <div class="col-md-4"><div class="pcr-stat"><div class="pcr-stat-value text-warning">{{ formatMoney(executiveSummary.totalVariance) }}</div><div class="pcr-stat-label">Total variance</div></div></div>
          </div>
          <h6 class="hr-mt-3">Spending entity summary (register departments)</h6>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Department</th><th>Employees</th><th>Basic pay</th><th>Gross payroll</th><th>Pension</th><th>NITA</th><th>Total cost</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (executiveSummary && executiveSummary.spendingEntitySummary) || []" :key="'ex-' + i">
                  <td>{{ r.department }}</td><td>{{ r.employeeCount }}</td>
                  <td>{{ formatMoney(r.basicPay) }}</td>
                  <td>{{ formatMoney(r.payrollCost) }}</td><td>{{ formatMoney(r.pensionCost) }}</td>
                  <td>{{ formatMoney(r.nitaCost) }}</td><td>{{ formatMoney(r.totalCost) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <p class="text-muted hr-small">Mandatory pension reconciliation: payroll deduction <strong>902</strong> vs pension file <strong>EDCode 902</strong>. Voluntary/AVC excluded.</p>
          <div class="row g-2 hr-mb-3" v-if="pensionRecon && pensionRecon.totals">
            <div class="col-md-3">Payroll 902: {{ formatMoney(pensionRecon.totals.payrollPension902) }}</div>
            <div class="col-md-3">Pension files: {{ formatMoney(pensionRecon.totals.pensionFiles902) }}</div>
            <div class="col-md-3">Variance: {{ formatMoney(pensionRecon.totals.variance) }}</div>
            <div class="col-md-3">Mismatches: {{ pensionRecon.totals.varianceCount }}</div>
          </div>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('pension')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Department</th><th>Payroll 902</th><th>Pension file</th><th>Variance</th><th>Status</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (pensionRecon && pensionRecon.variances) || []" :key="'pv-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.name }}</td><td>{{ r.department }}</td>
                  <td>{{ formatMoney(r.payrollPension) }}</td><td>{{ formatMoney(r.pensionFile) }}</td>
                  <td>{{ formatMoney(r.variance) }}</td><td>{{ r.status }}</td>
                </tr>
                <tr v-if="!(pensionRecon && pensionRecon.variances && pensionRecon.variances.length)"><td colspan="7" class="text-muted">No pension variances.</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <p class="text-muted hr-small">
            Gratuity total is taken from the <strong>TOTAL</strong> row GRATUITY column
            (not May Pay / basic). Uploaded total: {{ formatMoney(gratuityRecon && gratuityRecon.uploadedTotal) }}
            <span v-if="gratuityRecon && gratuityRecon.amountColumn" class="text-muted"> — column: {{ gratuityRecon.amountColumn }}</span>
          </p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('gratuity')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>ID</th><th>Name</th><th>Department</th><th>Amount</th><th>In register</th><th>In payroll</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in (gratuityRecon && gratuityRecon.uploadedRows) || []" :key="'gr-' + i">
                  <td>{{ displayId(r.rawId) }}</td><td>{{ r.name }}</td><td>{{ r.department }}</td>
                  <td>{{ formatMoney(r.amount) }}</td><td>{{ r.inRegister ? "Yes" : "No" }}</td><td>{{ r.inPayroll ? "Yes" : "No" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <div class="row g-3">
            <div class="col-md-3"><strong>Rate:</strong> {{ formatMoney(nitaPerEmployee) }} / employee</div>
            <div class="col-md-3"><strong>Count:</strong> {{ summary.employeeCount || 0 }}</div>
            <div class="col-md-3"><strong>Expected:</strong> {{ formatMoney(summary.nitaExpected) }}</div>
            <div class="col-md-3"><strong>Actual (ledger):</strong> {{ formatMoney(summary.nitaActual) }}</div>
            <div class="col-md-3"><strong>Variance:</strong> {{ formatMoney(summary.nitaVariance) }}</div>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <h6>Department variances ({{ deptVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 240px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Register dept</th><th>CSV dept</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in deptVariances" :key="'dv-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regName }}</td><td>{{ r.regDept }}</td><td>{{ r.csvDept }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Designation variances ({{ designationVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Register</th><th>CSV</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in designationVariances" :key="'des-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regDesignation }}</td><td>{{ r.csvDesignation }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Job group variances ({{ jobGroupVariances.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Register</th><th>CSV</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in jobGroupVariances" :key="'jg-' + i">
                  <td>{{ displayId(r.empNo) }}</td><td>{{ r.regJobGroup }}</td><td>{{ r.csvJobGroup }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Duplicate payroll IDs ({{ duplicateRows.length }})</h6>
          <div class="table-responsive hr-mb-3" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Occurrences</th><th>Total pay</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in duplicateRows" :key="'dup-' + i">
                  <td>{{ displayId(r.empNo || r.staffId) }}</td>
                  <td>{{ r.count }}</td>
                  <td>{{ formatMoney(r.totalPay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h6>Inactive on register but paid on payroll ({{ inactivePaidRows.length }})</h6>
          <div class="table-responsive" style="max-height: 200px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead><tr><th>Emp No</th><th>Name</th><th>Status</th><th>Pay</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in inactivePaidRows" :key="'inact-' + i">
                  <td>{{ displayId(r.empNo) }}</td>
                  <td>{{ r.regName || r.name }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <button type="button" class="hr-btn hr-btn-outline-secondary hr-btn-sm hr-mb-2" :disabled="auditLogLoading" @click="loadAuditLog">
            {{ auditLogLoading ? "Loading…" : "Refresh audit log" }}
          </button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr><th>When</th><th>Action</th><th>Actor</th><th>Details</th></tr>
              </thead>
              <tbody>
                <tr v-if="auditLogLoading"><td colspan="4" class="text-muted">Loading…</td></tr>
                <tr v-else-if="!auditLogs.length"><td colspan="4" class="text-muted">No audit entries for this period.</td></tr>
                <tr v-for="log in auditLogs" :key="'aud-' + log.id">
                  <td>{{ log.created_at }}</td>
                  <td>{{ log.action }}</td>
                  <td>{{ log.actor_username || "—" }}</td>
                  <td class="hr-small">{{ formatAuditDetails(log.details_json) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'spending'" class="table-card">
          <p class="text-muted hr-small">
            <strong>Spending Entity (Costings) Summary</strong> — layout matches <em>docus/Spending entity (Costings) Summary Template</em>.
            Grouped by register department. Includes TOTAL WAGE BILL (monthly / annual) with pension, NITA and gratuity TOTAL-row figure.
          </p>
          <p v-if="!spendingSummaryRows.length && analysisDone" class="text-warning hr-small">
            No matched employees to group by register department.
          </p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spending')">Download CSV</button>
          <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm hr-mb-2 hr-ms-1" @click="exportReportPdf('spending')">PDF (letterhead)</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>VOTE</th>
                  <th>County spending entity</th>
                  <th>Staff earning</th>
                  <th>Basic pay</th>
                  <th>Rental house all.</th>
                  <th>Commuter all.</th>
                  <th>Other allowances</th>
                  <th>Gross salary</th>
                  <th>NSSF</th>
                  <th>Housing levy</th>
                  <th>Pension (employer)</th>
                  <th>NITA</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in spendingSummaryRows" :key="'sp-' + row.seq + '-' + row.key">
                  <td>{{ row.seq }}</td>
                  <td>{{ row.vote || row.voteCode || "" }}</td>
                  <td>{{ row.entity }}</td>
                  <td>{{ row.staffCount }}</td>
                  <td>{{ formatMoney(row.basic) }}</td>
                  <td>{{ formatMoney(row.rental) }}</td>
                  <td>{{ formatMoney(row.commuter) }}</td>
                  <td>{{ formatMoney(row.otherAllow) }}</td>
                  <td>{{ formatMoney(row.gross) }}</td>
                  <td>{{ formatMoney(row.nssf) }}</td>
                  <td>{{ formatMoney(row.housing) }}</td>
                  <td>{{ formatMoney(row.pensionEmployer) }}</td>
                  <td>{{ formatMoney(row.nita) }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
                <tr v-if="spendingSummaryRows.length" class="table-secondary fw-bold">
                  <td colspan="3">TOTALS</td>
                  <td>{{ spendingTotals.staffCount }}</td>
                  <td>{{ formatMoney(spendingTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingTotals.nita) }}</td>
                  <td>{{ formatMoney(spendingTotals.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- TOTAL WAGE BILL bottom (docus template) -->
          <div v-if="spendingSummaryRows.length" class="wage-bill-bottom hr-mt-4">
            <h6 class="hr-mb-2">TOTAL WAGE BILL</h6>
            <div class="table-responsive" style="max-width: 640px;">
              <table class="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th class="text-end">Monthly</th>
                    <th class="text-end">Annual projection</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="fw-bold">
                    <td>TOTAL GROSS SALARY</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.gross) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.gross) }}</td>
                  </tr>
                  <tr class="table-light fw-bold">
                    <td colspan="3">EMPLOYER OBLIGATIONS</td>
                  </tr>
                  <tr>
                    <td class="ps-4">NSSF</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.nssf) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.nssf) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Housing Levy</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.housing) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.housing) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Pension (Employer)</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.pensionEmployer) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.pensionEmployer) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">NITA</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.nita) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.nita) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Gratuity <span class="text-muted hr-small">(from uploaded file total)</span></td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.gratuity) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.gratuity) }}</td>
                  </tr>
                  <tr class="fw-bold">
                    <td>TOTAL EMPLOYER OBLIGATIONS</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.employerObligations) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.employerObligations) }}</td>
                  </tr>
                  <tr class="table-primary fw-bold">
                    <td>TOTAL {{ periodLabel.toUpperCase() }} WAGE BILL</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.totalWageBill) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.totalWageBill) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'wagebill'" class="table-card">
          <p class="text-muted hr-small">
            <strong>Wagebill Summary</strong> — matches <em>docus/Wagebill summary Template</em> for this month (by spending entity).
            Use <em>Multi-month wage bill</em> after you analyse more months.
          </p>
          <div class="d-flex flex-wrap gap-2 hr-mb-2">
            <button type="button" class="hr-btn hr-btn-success hr-btn-sm" @click="exportTableCsv('wagebill')">Download CSV</button>
            <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm" @click="exportReportPdf('wagebill')">PDF (letterhead)</button>
          </div>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>COUNTY SPENDING ENTITY</th>
                  <th>Staff</th>
                  <th>Basic Pay</th>
                  <th>Gross Salary</th>
                  <th>Total cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in spendingSummaryRows" :key="'wb-' + (row.key || i)">
                  <td>{{ row.seq || i + 1 }}</td>
                  <td>{{ row.entity }}</td>
                  <td>{{ row.staffCount }}</td>
                  <td>{{ formatMoney(row.basic) }}</td>
                  <td>{{ formatMoney(row.gross) }}</td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
                <tr v-if="spendingSummaryRows.length" class="table-secondary fw-bold">
                  <td colspan="2">TOTALS</td>
                  <td>{{ spendingTotals.staffCount }}</td>
                  <td>{{ formatMoney(spendingTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingTotals.total) }}</td>
                </tr>
                <tr v-if="!spendingSummaryRows.length"><td colspan="6" class="text-muted">No wage bill rows.</td></tr>
              </tbody>
            </table>
          </div>
          <div v-if="spendingSummaryRows.length" class="wage-bill-bottom hr-mt-3">
            <h6 class="hr-mb-2">TOTAL WAGE BILL (same as docus bottom block)</h6>
            <div class="table-responsive" style="max-width: 640px;">
              <table class="table table-bordered table-sm">
                <thead>
                  <tr><th></th><th class="text-end">Monthly</th><th class="text-end">Annual projection</th></tr>
                </thead>
                <tbody>
                  <tr class="fw-bold">
                    <td>TOTAL GROSS SALARY</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.gross) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.gross) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">NSSF</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.nssf) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.nssf) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Housing Levy</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.housing) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.housing) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Pension (Employer)</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.pensionEmployer) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.pensionEmployer) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">NITA</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.nita) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.nita) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-4">Gratuity (TOTAL row)</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.gratuity) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.gratuity) }}</td>
                  </tr>
                  <tr class="table-primary fw-bold">
                    <td>TOTAL {{ periodLabel.toUpperCase() }} WAGE BILL</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.monthly.totalWageBill) }}</td>
                    <td class="text-end">{{ formatMoney(wageBillBottomSummary.annual.totalWageBill) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-show="activeTab === 'spendingDetail'" class="table-card">
          <p class="text-muted hr-small"><strong>Spending Entities Detailed Report</strong> — employee-level costing by register department (spending entity).</p>
          <div class="d-flex flex-wrap gap-2 hr-mb-2">
            <select v-model="selectedSpendingEntityDetail" class="filter-select" style="max-width: 360px;">
              <option value="">All spending entities</option>
              <option v-for="e in spendingEntityOptions" :key="'seo-' + e" :value="e">{{ e }}</option>
            </select>
          </div>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('spendingDetail')">Download CSV</button>
          <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm hr-mb-2 hr-ms-1" @click="exportReportPdf('spendingDetail')">PDF (letterhead)</button>
          <div class="table-responsive" style="max-height: 520px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>PAYROLL No.</th>
                  <th>ID. No.</th>
                  <th>NAME</th>
                  <th>COUNTY SPENDING ENTITY</th>
                  <th>Basic Pay</th>
                  <th>Rental House All.</th>
                  <th>Commuter All.</th>
                  <th>Other Allowances</th>
                  <th>GrossPay</th>
                  <th>Projected Annual Increment (4% of Basic Pay)</th>
                  <th>NSSF</th>
                  <th>H/Levy</th>
                  <th>Pension (Employer)</th>
                  <th>NITA</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in filteredSpendingDetailRows" :key="'sdr-' + i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ r.payrollNo }}</td>
                  <td>{{ r.nationalId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.entity }}</td>
                  <td>{{ formatMoney(r.basic) }}</td>
                  <td>{{ formatMoney(r.rental) }}</td>
                  <td>{{ formatMoney(r.commuter) }}</td>
                  <td>{{ formatMoney(r.otherAllow) }}</td>
                  <td>{{ formatMoney(r.gross) }}</td>
                  <td>{{ formatMoney(r.projectedIncrement) }}</td>
                  <td>{{ formatMoney(r.nssf) }}</td>
                  <td>{{ formatMoney(r.housing) }}</td>
                  <td>{{ formatMoney(r.pensionEmployer) }}</td>
                  <td>{{ formatMoney(r.nita) }}</td>
                </tr>
                <tr v-if="filteredSpendingDetailRows.length" class="table-secondary fw-bold">
                  <td colspan="5">Totals</td>
                  <td>{{ formatMoney(spendingDetailTotals.basic) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.rental) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.commuter) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.otherAllow) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.gross) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.projectedIncrement) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nssf) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.housing) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.pensionEmployer) }}</td>
                  <td>{{ formatMoney(spendingDetailTotals.nita) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'registerOnly'" class="table-card">
          <p class="text-muted hr-small"><strong>Missing from Payroll:</strong> employees in HRMIS (register) not found on the payroll file (matched by Payroll / Emp No or National ID).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('registerOnly')">Download CSV</button>
          <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm hr-mb-2 hr-ms-1" @click="exportReportPdf('registerOnly')">PDF (letterhead)</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>National ID</th>
                  <th>Name</th>
                  <th>Department (register)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(e, i) in registerOnlyRows" :key="'ro-' + i">
                  <td>{{ e.empNo }}</td>
                  <td>{{ e.nationalId }}</td>
                  <td>{{ fullName(e) }}</td>
                  <td>{{ e.departmentName || e.departmentID }}</td>
                  <td>{{ e.status }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="activeTab === 'csvOnly'" class="table-card">
          <p class="text-muted hr-small"><strong>Extra from Payroll:</strong> payroll rows whose staff ID did not match any employee (Emp No or National ID).</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('csvOnly')">Download CSV</button>
          <button type="button" class="hr-btn hr-btn-outline-success hr-btn-sm hr-mb-2 hr-ms-1" @click="exportReportPdf('csvOnly')">PDF (letterhead)</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Staff ID (CSV)</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Pay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in csvOnlyRows" :key="'co-' + i">
                  <td>{{ r.staffId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.dept }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="false" class="table-card">
          <p class="text-muted hr-small">Rows where CSV staff ID matched the register.</p>
          <button type="button" class="hr-btn hr-btn-success hr-btn-sm hr-mb-2" @click="exportTableCsv('matched')">Download CSV</button>
          <div class="table-responsive" style="max-height: 480px; overflow: auto;">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Emp No</th>
                  <th>Name (register)</th>
                  <th>Dept (register)</th>
                  <th>Dept (CSV)</th>
                  <th>Dept mismatch</th>
                  <th>Pay (CSV)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in matchedDetailRows" :key="'m-' + i">
                  <td>{{ r.empNo }}</td>
                  <td>{{ r.regName }}</td>
                  <td>{{ r.regDept }}</td>
                  <td>{{ r.csvDept }}</td>
                  <td>{{ r.deptMismatch ? 'Yes' : '' }}</td>
                  <td>{{ formatMoney(r.pay) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  loadCountyLogoDataUrl,
  exportBrandedTablePdf,
  exportSpendingEntityCostingsPdf,
  formatMoney as formatMoneyPdf
} from "@/utils/payrollReconPdf";
import {
  normId,
  pickColumn,
  pickLedgerColumn,
  parseMoney,
  detectCodeColumns,
  runRegisterPayrollAnalysis,
  reconcilePension,
  reconcileGratuity,
  buildExecutiveSummary,
  maskSensitive,
  buildRegisterIndex,
  resolveSpendingEntity,
  resolveRegisterVote,
  resolvePayrollCostColumns,
  buildWageBillBottomSummary,
  validateGratuityFileForAnalysis,
  matrixToHeaderedRows
} from "@/utils/payrollReconciliationEngine";
import { resolveReportGeneratorMeta, reportGeneratorCsvLines } from "@/utils/reportGeneratorMeta";

/** Excel/CSV matrix → rows; skips county title rows above real headers (Homabay gratuity schedule). */
function rowsFromSheetMatrix(matrix) {
  const parsed = matrixToHeaderedRows(matrix || []);
  return {
    rows: parsed.rows || [],
    headers: parsed.headers || [],
    headerRowIndex: parsed.headerRowIndex
  };
}

function normKeyHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export default {
  name: "PayrollCsvReconciliation",
  components: { HrPageLayout },
  data() {
    return {
      breadcrumbItems: [
        { label: "Reports & Analytics", route: "/reports-hub" },
        { label: "Payroll ledger reconciliation" }
      ],
      csvRows: [],
      headers: [],
      registerScope: "active",
      loading: false,
      errorMsg: "",
      analysisDone: false,
      activeTab: "saved",
      mapId: "",
      mapDept: "",
      mapPay: "",
      mapName: "",
      selectedSpendingEntityDetail: "",
      periodYear: new Date().getFullYear(),
      periodMonth: new Date().getMonth() + 1,
      nitaPerEmployee: 50,
      savingNita: false,
      periodFinalized: false,
      periodId: null,
      canManage: true,
      canFinalize: false,
      canReopen: false,
      maskSensitiveData: false,
      preValidation: null,
      uploadStatus: {},
      stagedFiles: {},
      uploadingAll: false,
      uploadProgress: 0,
      uploadErrors: [],
      pensionRows: { '16': [], '32': [], '33': [], '101': [] },
      pensionHeaders: { '16': [], '32': [], '33': [], '101': [] },
      gratuityRows: [],
      gratuityHeaders: [],
      detectedEarnings: [],
      detectedDeductions: [],
      pensionRecon: null,
      gratuityRecon: null,
      executiveSummary: null,
      financialSummary: null,
      deptVariances: [],
      designationVariances: [],
      jobGroupVariances: [],
      duplicateRows: [],
      inactivePaidRows: [],
      registerIndex: null,
      employees: [],
      savedReports: [],
      savedReportsLoading: false,
      savingReport: false,
      saveReportMessage: "",
      saveReportError: false,
      summary: {
        csvRowCount: 0,
        registerCount: 0,
        matchedCount: 0,
        csvOnlyCount: 0,
        registerOnlyCount: 0,
        totalCsvPay: 0
      },
      wageBillRows: [],
      registerOnlyRows: [],
      csvOnlyRows: [],
      matchedDetailRows: [],
      spendingSummaryRows: [],
      spendingDetailRows: [],
      tabs: [
        { id: "saved", label: "Saved reports", requiresAnalysis: false },
        { id: "repository", label: "Monthly file repository", requiresAnalysis: false },
        { id: "spending", label: "Spending Entity (Costings) Summary", requiresAnalysis: true },
        { id: "spendingDetail", label: "Spending Entities Detailed Report", requiresAnalysis: true },
        { id: "wagebill", label: "Wagebill Summary", requiresAnalysis: true },
        { id: "wagebillMulti", label: "Multi-month wage bill", requiresAnalysis: false },
        { id: "registerOnly", label: "Missing from Payroll", requiresAnalysis: true },
        { id: "csvOnly", label: "Extra from Payroll", requiresAnalysis: true }
      ],
      auditLogs: [],
      auditLogLoading: false,
      hydratingFiles: false,
      downloadingUpload: "",
      deptNameById: null,
      deptVoteById: null,
      multiMonthWage: { months: [], entities: [], loading: false, year: null },
      letterhead: null
    };
  },
  computed: {
    multiMonthColumns() {
      const months = this.multiMonthWage.months || [];
      return months.map((m) => {
        const found = this.monthOptions.find((x) => x.value === Number(m));
        return { value: Number(m), label: found ? found.label.slice(0, 3) : String(m) };
      });
    },
    wageBillBottomSummary() {
      const g =
        this.gratuityRecon?.uploadedTotal != null
          ? this.gratuityRecon.uploadedTotal
          : this.executiveSummary?.totalGratuityCost || 0;
      return buildWageBillBottomSummary(this.spendingSummaryRows, g);
    },
    monthOptions() {
      return [
        { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
        { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
        { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
      ];
    },
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1];
    },
    periodLabel() {
      const m = this.monthOptions.find((x) => x.value === this.periodMonth);
      return `${m ? m.label : this.periodMonth} ${this.periodYear}`;
    },
    pensionFilesReady() {
      if (!this.preValidation || !this.preValidation.checks) return false;
      return this.preValidation.checks.pension_16 && this.preValidation.checks.pension_32 &&
        this.preValidation.checks.pension_33 && this.preValidation.checks.pension_101;
    },
    canRunAnalysis() {
      if (this.periodFinalized || this.hydratingFiles || !this.canManage) return false;
      const pensionLocal = [16, 32, 33, 101].every((c) => (this.pensionRows[c] || []).length > 0);
      const gratuityLocal = (this.gratuityRows || []).length > 0 && this.gratuityTotalRowReady;
      return this.csvRows.length > 0 && pensionLocal && gratuityLocal && Number(this.nitaPerEmployee) > 0;
    },
    gratuityTotalRowReady() {
      if (!(this.gratuityRows || []).length) return false;
      return !!validateGratuityFileForAnalysis(this.gratuityRows, this.gratuityHeaders).ok;
    },
    spendingTotals() {
      const z = {
        staffCount: 0,
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0,
        total: 0
      };
      for (const row of this.spendingSummaryRows) {
        z.staffCount += Number(row.staffCount) || 0;
        z.basic += Number(row.basic) || 0;
        z.rental += Number(row.rental) || 0;
        z.commuter += Number(row.commuter) || 0;
        z.otherAllow += Number(row.otherAllow) || 0;
        z.gross += Number(row.gross) || 0;
        z.nssf += Number(row.nssf) || 0;
        z.housing += Number(row.housing) || 0;
        z.pensionEmployer += Number(row.pensionEmployer) || 0;
        z.nita += Number(row.nita) || 0;
        z.total += Number(row.total) || 0;
      }
      return z;
    },
    spendingEntityOptions() {
      const set = new Set();
      // Prefer all entities seen in summary + detail after full-register analysis
      (this.spendingSummaryRows || []).forEach((r) => {
        const k = String(r.entity || r.department || r.name || "").trim();
        if (k) set.add(k);
      });
      (this.spendingDetailRows || []).forEach((r) => {
        const k = String(r.entity || "").trim();
        if (k) set.add(k);
      });
      // Also pull names from register map so filter has full department list even if some rows cap
      if (this.deptNameById && typeof this.deptNameById === "object") {
        Object.values(this.deptNameById).forEach((n) => {
          const k = String(n || "").trim();
          if (k) set.add(k);
        });
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },
    filteredSpendingDetailRows() {
      const selected = String(this.selectedSpendingEntityDetail || "").trim();
      if (!selected) return this.spendingDetailRows || [];
      return (this.spendingDetailRows || []).filter((r) => String(r.entity || "").trim() === selected);
    },
    spendingDetailTotals() {
      const z = {
        basic: 0,
        rental: 0,
        commuter: 0,
        otherAllow: 0,
        gross: 0,
        projectedIncrement: 0,
        nssf: 0,
        housing: 0,
        pensionEmployer: 0,
        nita: 0
      };
      for (const r of this.filteredSpendingDetailRows || []) {
        z.basic += Number(r.basic) || 0;
        z.rental += Number(r.rental) || 0;
        z.commuter += Number(r.commuter) || 0;
        z.otherAllow += Number(r.otherAllow) || 0;
        z.gross += Number(r.gross) || 0;
        z.projectedIncrement += Number(r.projectedIncrement) || 0;
        z.nssf += Number(r.nssf) || 0;
        z.housing += Number(r.housing) || 0;
        z.pensionEmployer += Number(r.pensionEmployer) || 0;
        z.nita += Number(r.nita) || 0;
      }
      return z;
    }
  },
  mounted() {
    this.loadCapabilities();
    this.loadPeriodStatus();
    this.loadSavedReports();
    this.loadLetterhead();
  },
  methods: {
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          // build absolute URLs for logos when API returns relative paths
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try {
              return new URL(u, authUrl).href;
            } catch (_) {
              return u;
            }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    displayId(v) {
      return maskSensitive(v, this.maskSensitiveData);
    },
    async loadCapabilities() {
      try {
        const data = new FormData();
        data.append("function", "getPayrollReconCapabilities");
        const res = await execute(data, authUrl);
        if (res.data && res.data.success && res.data.data) {
          this.canManage = !!res.data.data.canManage;
          this.canFinalize = !!res.data.data.canFinalize;
          this.canReopen = !!res.data.data.canReopen;
          this.maskSensitiveData = !!res.data.data.maskSensitive;
        } else if (res.data && !res.data.success) {
          // Server denied — not a network error
          this.canManage = false;
          const msg = res.data.message || "Permission check failed.";
          if (!msg.toLowerCase().includes("permission") && !msg.toLowerCase().includes("unauthorized")) {
            this.errorMsg = "Could not load upload permissions: " + msg;
          }
        }
        // If response has no data at all, keep canManage = true (optimistic — server will enforce)
      } catch (e) {
        // Network / parse error — keep canManage = true so uploads can still be attempted
        console.warn("loadCapabilities failed:", e);
      }
    },
    async loadPeriodStatus() {
      try {
        const data = new FormData();
        data.append("function", "getPayrollReconPeriodStatus");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success && res.data.data) {
          const d = res.data.data;
          this.periodFinalized = !!d.finalized;
          this.periodId = d.period ? d.period.id : null;
          this.preValidation = d.validation || null;
          if (d.nita && d.nita.amount_per_employee != null) {
            this.nitaPerEmployee = Number(d.nita.amount_per_employee);
          }
          const uploads = d.uploads || [];
          const st = {};
          uploads.forEach((u) => {
            // tolerate file_type / fileType / original_name / originalName column variants
            const ft = u.file_type || u.fileType || u.FILE_TYPE || "";
            const name = u.original_name || u.originalName || u.ORIGINAL_NAME || u.stored_path || "";
            if (ft && !st[ft]) st[ft] = name || "(uploaded)";
          });
          // Do not wipe optimistic names if server returned empty array incorrectly for same files just uploaded
          if (Object.keys(st).length > 0 || uploads.length === 0) {
            this.uploadStatus = st;
          }
          await this.hydrateFilesFromServer();
        } else {
          const msg = res.data?.message || "Could not load monthly file repository status.";
          // Keep existing uploadStatus rather than falsely marking all as not uploaded
          if (res.data && res.data.success === false) {
            console.warn("loadPeriodStatus:", msg);
            if (!Object.keys(this.uploadStatus || {}).length) {
              this.errorMsg = msg;
            }
          }
        }
      } catch (e) {
        this.preValidation = null;
        console.warn("loadPeriodStatus failed", e);
        if (!Object.keys(this.uploadStatus || {}).length) {
          this.errorMsg = e?.message || "Could not load period file repository status.";
        }
      }
    },
    async onPeriodChange() {
      this.analysisDone = false;
      this.csvRows = [];
      this.headers = [];
      this.pensionRows = { '16': [], '32': [], '33': [], '101': [] };
      this.pensionHeaders = { '16': [], '32': [], '33': [], '101': [] };
      this.gratuityRows = [];
      this.gratuityHeaders = [];
      this.stagedFiles = {};
      this.uploadErrors = [];
      this.uploadProgress = 0;
      await this.loadPeriodStatus();
    },
    formatReportPeriod(r) {
      const y = Number(r.period_year);
      const m = Number(r.period_month);
      if (!y || !m) return "—";
      const mo = this.monthOptions.find((x) => x.value === m);
      return `${mo ? mo.label : m} ${y}`;
    },
    formatAuditDetails(raw) {
      if (!raw) return "—";
      try {
        const o = typeof raw === "string" ? JSON.parse(raw) : raw;
        return JSON.stringify(o);
      } catch (_) {
        return String(raw);
      }
    },
    async loadAuditLog() {
      this.auditLogLoading = true;
      try {
        const data = new FormData();
        data.append("function", "listPayrollReconAuditLog");
        if (this.periodId) data.append("periodId", String(this.periodId));
        data.append("limit", "100");
        const res = await execute(data, authUrl);
        this.auditLogs = res.data?.data?.logs || [];
      } catch (_) {
        this.auditLogs = [];
      } finally {
        this.auditLogLoading = false;
      }
    },
    parseRowsFromBuffer(buffer, fileName) {
      const name = (fileName || "").toLowerCase();
      if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".xlsm")) {
        const data = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: false
        });
        const { rows, headers } = rowsFromSheetMatrix(matrix);
        return { rows, headers };
      }
      const text = new TextDecoder("utf-8").decode(buffer);
      const matrixParsed = Papa.parse(text, { header: false, skipEmptyLines: "greedy" });
      const matrix = matrixParsed.data || [];
      if (matrix.length > 1 && /gratuity|grat/i.test(name)) {
        const { rows, headers } = rowsFromSheetMatrix(matrix);
        if (rows.length && (headers || []).some((h) => /gratuity/i.test(String(h || "")))) {
          return { rows, headers };
        }
      }
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: "greedy" });
      const rows = (parsed.data || []).filter((r) => Object.keys(r).some((k) => String(r[k] || "").trim() !== ""));
      return { rows, headers: parsed.meta.fields || (rows[0] ? Object.keys(rows[0]) : []) };
    },
    async fetchUploadContent(fileType) {
      const data = new FormData();
      data.append("function", "getPayrollReconUploadContent");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("fileType", fileType);
      const res = await execute(data, authUrl);
      if (!res.data || !res.data.success || !res.data.data) {
        throw new Error(res.data?.message || `Could not load ${fileType}`);
      }
      return res.data.data;
    },
    async hydrateFilesFromServer() {
      const types = ["payroll", "pension_16", "pension_32", "pension_33", "pension_101", "gratuity"];
      const need = types.filter((t) => {
        if (!this.uploadStatus[t]) return false;
        if (t === "payroll" && this.csvRows && this.csvRows.length > 0) return false;
        if (t === "gratuity" && this.gratuityRows && this.gratuityRows.length > 0) return false;
        if (t.startsWith("pension_")) {
          const code = t.replace("pension_", "");
          if (this.pensionRows[code] && this.pensionRows[code].length > 0) return false;
        }
        return true;
      });
      if (!need.length) return;
      this.hydratingFiles = true;
      try {
        for (const fileType of need) {
          const payload = await this.fetchUploadContent(fileType);
          const bin = atob(payload.contentBase64 || "");
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const { rows, headers } = this.parseRowsFromBuffer(bytes, payload.originalName || fileType);
          if (fileType === "payroll") {
            this.csvRows = rows;
            this.headers = headers;
            this.autoMapColumns();
            const codes = detectCodeColumns(headers);
            this.detectedEarnings = codes.earnings;
            this.detectedDeductions = codes.deductions;
          } else if (fileType.startsWith("pension_")) {
            const code = fileType.replace("pension_", "");
            if (["16", "32", "33", "101"].includes(code)) {
              this.pensionRows[code] = rows;
              this.pensionHeaders[code] = headers;
            }
          } else if (fileType === "gratuity") {
            const check = validateGratuityFileForAnalysis(rows, headers);
            if (!check.ok) {
              this.gratuityRows = [];
              this.gratuityHeaders = [];
              this.errorMsg = check.message;
              window.alert(check.message);
              continue;
            }
            this.gratuityRows = rows;
            this.gratuityHeaders = headers;
          }
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.hydratingFiles = false;
      }
    },
    async saveNitaConfig() {
      if (!this.nitaPerEmployee || this.nitaPerEmployee <= 0) {
        this.errorMsg = "Enter a valid NITA amount per employee.";
        return;
      }
      this.savingNita = true;
      try {
        const data = new FormData();
        data.append("function", "savePayrollReconNita");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("amountPerEmployee", String(this.nitaPerEmployee));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) throw new Error(res.data?.message || "Failed to save NITA");
        await this.loadPeriodStatus();
        this.saveReportMessage = "NITA amount saved.";
        this.saveReportError = false;
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.savingNita = false;
      }
    },
    async uploadFileToServer(file, fileType, rowCount, headers) {
      const data = new FormData();
      data.append("function", "uploadPayrollReconFile");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("fileType", fileType);
      data.append("file", file);
      data.append("rowCount", String(rowCount || 0));
      data.append("headersJson", JSON.stringify(headers || []));
      const res = await execute(data, authUrl);
      if (!res.data || !res.data.success) {
        throw new Error(res.data?.message || "Upload failed");
      }
      return res.data.data;
    },
    parseFile(file) {
      return new Promise((resolve, reject) => {
        const name = (file.name || "").toLowerCase();
        if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".xlsm")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const data = new Uint8Array(e.target.result);
              const wb = XLSX.read(data, { type: "array", cellDates: true });
              const sheet = wb.Sheets[wb.SheetNames[0]];
              // header:1 + auto-detect real column row (title banners above SNO/IDNUM/GRATUITY)
              const matrix = XLSX.utils.sheet_to_json(sheet, {
                header: 1,
                defval: "",
                raw: false,
                blankrows: false
              });
              const { rows, headers } = rowsFromSheetMatrix(matrix);
              resolve({ rows, headers });
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = () => reject(new Error("Could not read file"));
          reader.readAsArrayBuffer(file);
          return;
        }
        // CSV: for gratuity-style multi-title sheets, detect header row; else classic header:true
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            const matrixParsed = Papa.parse(text, {
              header: false,
              skipEmptyLines: "greedy"
            });
            const matrix = matrixParsed.data || [];
            if (matrix.length > 1) {
              const detected = rowsFromSheetMatrix(matrix);
              const hasGrat = (detected.headers || []).some((h) =>
                /gratuity/i.test(String(h || ""))
              );
              if (hasGrat || (detected.headerRowIndex != null && detected.headerRowIndex > 0)) {
                resolve({ rows: detected.rows, headers: detected.headers });
                return;
              }
            }
            const parsed = Papa.parse(text, {
              header: true,
              skipEmptyLines: "greedy",
              transformHeader: (h) => String(h || "").trim()
            });
            const rows = (parsed.data || []).filter((r) =>
              Object.keys(r).some((k) => String(r[k] || "").trim() !== "")
            );
            resolve({
              rows,
              headers: parsed.meta.fields || (rows[0] ? Object.keys(rows[0]) : [])
            });
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsText(file);
      });
    },
    stageFile(ev, type) {
      const f = ev.target.files && ev.target.files[0];
      if (!f) {
        const newStaged = { ...this.stagedFiles };
        delete newStaged[type];
        this.stagedFiles = newStaged;
        return;
      }
      // Gratuity: reject immediately if missing TOTAL / GRAND TOTAL row with gratuity figure
      if (type === "gratuity") {
        this.parseFile(f)
          .then(({ rows, headers }) => {
            const check = validateGratuityFileForAnalysis(rows, headers);
            if (!check.ok) {
              window.alert(check.message);
              this.errorMsg = check.message;
              if (ev.target) ev.target.value = "";
              const newStaged = { ...this.stagedFiles };
              delete newStaged.gratuity;
              this.stagedFiles = newStaged;
              return;
            }
            this.stagedFiles = { ...this.stagedFiles, gratuity: f };
            // Pre-load rows so analysis can run after other uploads
            this.gratuityRows = rows;
            this.gratuityHeaders = headers;
            this.errorMsg = "";
            this.analysisDone = false;
          })
          .catch((err) => {
            const msg = err?.message || "Could not read gratuity file.";
            window.alert(msg);
            this.errorMsg = msg;
            if (ev.target) ev.target.value = "";
          });
        return;
      }
      this.stagedFiles = { ...this.stagedFiles, [type]: f };
      this.errorMsg = "";
      this.analysisDone = false;
    },
    async uploadAllStagedFiles() {
      if (this.periodFinalized) {
        this.errorMsg = "This month is finalized. Uploads are blocked. A Super Admin must reopen the month first.";
        return;
      }
      const types = Object.keys(this.stagedFiles);
      if (types.length === 0) return;
      
      this.uploadingAll = true;
      this.uploadErrors = [];
      this.uploadProgress = 0;
      this.errorMsg = "";
      
      let completedCount = 0;
      
      for (const type of types) {
        const f = this.stagedFiles[type];
        try {
          const { rows, headers } = await this.parseFile(f);
          
          if (type === "payroll") {
            this.csvRows = rows;
            this.headers = headers;
            this.autoMapColumns();
            const codes = detectCodeColumns(headers);
            this.detectedEarnings = codes.earnings;
            this.detectedDeductions = codes.deductions;
          } else if (type.startsWith("pension_")) {
            const index = type.replace("pension_", "");
            this.pensionRows[index] = rows;
            this.pensionHeaders[index] = headers;
          } else if (type === "gratuity") {
            const check = validateGratuityFileForAnalysis(rows, headers);
            if (!check.ok) {
              throw new Error(check.message);
            }
            this.gratuityRows = rows;
            this.gratuityHeaders = headers;
          }
          
          await this.uploadFileToServer(f, type, rows.length, headers);
          
          this.uploadStatus = { ...this.uploadStatus, [type]: f.name };
          const newStaged = { ...this.stagedFiles };
          delete newStaged[type];
          this.stagedFiles = newStaged;
        } catch (e) {
          const typeLabel = type === "payroll" ? "Payroll ledger" : 
                            type === "gratuity" ? "Gratuity report" : 
                            `Pension file ${type.replace("pension_", "")}`;
          this.uploadErrors.push(`${typeLabel} (${f.name}): ${e.message || String(e)}`);
          if (type === "gratuity") {
            this.gratuityRows = [];
            this.gratuityHeaders = [];
            window.alert(e.message || String(e));
          }
        }
        
        completedCount++;
        this.uploadProgress = Math.round((completedCount / types.length) * 100);
      }
      
      await this.loadPeriodStatus();
      this.uploadingAll = false;
      setTimeout(() => {
        if (this.uploadProgress === 100 && this.uploadErrors.length === 0) {
          this.uploadProgress = 0;
        }
      }, 3000);
    },
    async finalizeMonth() {
      if (!window.confirm(`Finalize ${this.periodLabel}? This locks uploads and re-analysis.`)) return;
      const data = new FormData();
      data.append("function", "finalizePayrollReconMonth");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      const res = await execute(data, authUrl);
      if (res.data && res.data.success) {
        await this.loadPeriodStatus();
        this.saveReportMessage = res.data.message || "Month finalized.";
      } else {
        this.errorMsg = res.data?.message || "Could not finalize.";
      }
    },
    async reopenMonth() {
      const reason = window.prompt("Reason for reopening this month (required):");
      if (!reason || !reason.trim()) return;
      const data = new FormData();
      data.append("function", "reopenPayrollReconMonth");
      data.append("periodYear", String(this.periodYear));
      data.append("periodMonth", String(this.periodMonth));
      data.append("reason", reason.trim());
      const res = await execute(data, authUrl);
      if (res.data && res.data.success) {
        await this.loadPeriodStatus();
        this.saveReportMessage = "Month reopened.";
      } else {
        this.errorMsg = res.data?.message || "Could not reopen.";
      }
    },
    async saveTemplateCatalog(codes) {
      if (!codes || !codes.length) return;
      try {
        const data = new FormData();
        data.append("function", "savePayrollReconTemplateCatalog");
        data.append("codesJson", JSON.stringify(codes));
        await execute(data, authUrl);
      } catch (_) { /* non-blocking */ }
    },
    fullName(e) {
      const m = e.middleName ? ` ${e.middleName} ` : " ";
      return `${e.firstName || ""}${m}${e.lastName || ""}`.trim();
    },
    formatMoney(n) {
      if (n == null || Number.isNaN(n)) return "—";
      return new Intl.NumberFormat("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    onTabClick(id) {
      const t = this.tabs.find((x) => x.id === id);
      if (t && t.requiresAnalysis && !this.analysisDone) return;
      this.activeTab = id;
      if (id === "saved") this.loadSavedReports();
      if (id === "audit") this.loadAuditLog();
      if (id === "wagebillMulti") this.loadMultiMonthWageBill();
    },
    entityYtd(entityRow) {
      if (!entityRow || !entityRow.months) return 0;
      return Object.values(entityRow.months).reduce((s, m) => s + (Number(m.total) || 0), 0);
    },
    async downloadOriginalUpload(fileType) {
      this.downloadingUpload = fileType;
      this.errorMsg = "";
      try {
        const data = new FormData();
        data.append("function", "downloadPayrollReconUpload");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("fileType", fileType);
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success || !res.data.data) {
          throw new Error(res.data?.message || "Download failed");
        }
        const payload = res.data.data;
        const bin = atob(payload.contentBase64 || "");
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
        const mime = payload.mimeType || "application/octet-stream";
        const blob = new Blob([bytes], { type: mime });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = payload.originalName || `${fileType}_${this.periodYear}_${this.periodMonth}`;
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.downloadingUpload = "";
      }
    },
    async ensureDeptNameMap() {
      if (this.deptNameById instanceof Map && this.deptNameById.size) return this.deptNameById;
      try {
        const data = new FormData();
        data.append("function", "getDepartments");
        const res = await execute(data, authUrl);
        const list =
          res?.data?.data?.departments ||
          res?.data?.departments ||
          [];
        const map = new Map();
        const voteMap = new Map();
        (Array.isArray(list) ? list : []).forEach((d) => {
          const id = d.id ?? d.departmentID ?? d.departmentId;
          const name = d.departmentName || d.name || d.department;
          // Register Vote = departments.code (labelled Vote in Department Management)
          const vote = d.code || d.departmentCode || d.vote || d.voteCode || "";
          if (id != null && name) map.set(String(id), String(name).trim());
          if (id != null && vote) voteMap.set(String(id), String(vote).trim());
        });
        this.deptNameById = map;
        this.deptVoteById = voteMap;
        return map;
      } catch (_) {
        this.deptNameById = new Map();
        this.deptVoteById = new Map();
        return this.deptNameById;
      }
    },
    async saveWageBillSnapshot() {
      if (!this.spendingSummaryRows.length) return;
      try {
        const rows = this.spendingSummaryRows.map((r) => ({
          entity: r.entity,
          staffCount: r.staffCount,
          basic: r.basic,
          rental: r.rental,
          commuter: r.commuter,
          otherAllow: r.otherAllow,
          gross: r.gross,
          nssf: r.nssf,
          housing: r.housing,
          pensionEmployer: r.pensionEmployer,
          nita: r.nita,
          total: r.total
        }));
        const data = new FormData();
        data.append("function", "savePayrollReconWageBillSnapshot");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("rowsJson", JSON.stringify(rows));
        await execute(data, authUrl);
      } catch (e) {
        console.warn("Wage bill snapshot save failed", e);
      }
    },
    async loadMultiMonthWageBill() {
      this.multiMonthWage.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getPayrollReconWageBillYear");
        data.append("periodYear", String(this.periodYear));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success && res.data.data) {
          this.multiMonthWage = {
            months: res.data.data.months || [],
            entities: res.data.data.entities || [],
            loading: false,
            year: res.data.data.periodYear || this.periodYear
          };
        } else {
          this.multiMonthWage = { months: [], entities: [], loading: false, year: this.periodYear };
        }
      } catch (_) {
        this.multiMonthWage = { months: [], entities: [], loading: false, year: this.periodYear };
      } finally {
        this.multiMonthWage.loading = false;
      }
    },
    exportMultiMonthWageCsv() {
      this.ensureLetterheadForCsv();
      const cols = this.multiMonthColumns;
      const hdr = ["Spending entity", ...cols.map((c) => `${c.label} ${this.multiMonthWage.year}`), "YTD total"];
      const body = (this.multiMonthWage.entities || []).map((e) => {
        const cells = cols.map((c) => {
          const m = e.months && e.months[c.value];
          return m ? m.total : 0;
        });
        return [this.csvEscape(e.entity), ...cells, this.entityYtd(e)].join(",");
      });
      this.downloadBlob(
        [...this.csvLetterheadLines("Multi-Month Wage Bill"), hdr.join(","), ...body].join("\n"),
        `wage_bill_multimonth_${this.multiMonthWage.year || this.periodYear}.csv`
      );
    },
    async loadSavedReports() {
      this.savedReportsLoading = true;
      try {
        const data = new FormData();
        data.append("function", "listPayrollCsvReconciliationReports");
        data.append("limit", "50");
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.savedReports = res.data.data?.reports || [];
        } else {
          this.savedReports = [];
        }
      } catch (_) {
        this.savedReports = [];
      } finally {
        this.savedReportsLoading = false;
      }
    },
    openSaveReport() {
      this.saveReportMessage = "";
      const def = `Payroll reconciliation ${new Date().toISOString().slice(0, 10)}`;
      const title = window.prompt("Title for this saved report", def);
      if (title === null) return;
      this.saveReportToServer(title.trim() || def);
    },
    buildReportPayload(options = {}) {
      const slim = options.slim === true;
      const cap = (arr, n = 200) => (Array.isArray(arr) ? arr.slice(0, n) : []);
      const base = {
        version: 2,
        periodYear: this.periodYear,
        periodMonth: this.periodMonth,
        summary: { ...this.summary },
        financialSummary: this.financialSummary,
        executiveSummary: this.executiveSummary,
        pensionRecon: this.pensionRecon
          ? {
              totals: this.pensionRecon.totals,
              variances: slim ? cap(this.pensionRecon.variances, 100) : this.pensionRecon.variances,
              matchedCount: this.pensionRecon.totals?.matchedCount,
              pensionRecordCount: this.pensionRecon.pensionRecordCount
            }
          : null,
        gratuityRecon: this.gratuityRecon
          ? {
              uploadedTotal: this.gratuityRecon.uploadedTotal,
              missingCount: this.gratuityRecon.missingCount,
              uploadedRows: slim ? cap(this.gratuityRecon.uploadedRows, 100) : this.gratuityRecon.uploadedRows
            }
          : null,
        spendingSummaryRows: this.spendingSummaryRows,
        wageBillRows: this.wageBillRows,
        columnMaps: {
          mapId: this.mapId,
          mapDept: this.mapDept,
          mapPay: this.mapPay,
          mapName: this.mapName
        },
        headers: [...this.headers],
        nitaPerEmployee: this.nitaPerEmployee
      };
      if (slim) {
        base.registerOnlyRows = cap(this.registerOnlyRows, 50);
        base.csvOnlyRows = cap(this.csvOnlyRows, 50);
        base.matchedDetailRows = cap(this.matchedDetailRows, 100);
        base.spendingDetailRows = cap(this.spendingDetailRows, 100);
        base.deptVariances = cap(this.deptVariances, 50);
        base.duplicateRows = cap(this.duplicateRows, 50);
        base.inactivePaidRows = cap(this.inactivePaidRows, 50);
      } else {
        base.registerOnlyRows = this.registerOnlyRows;
        base.csvOnlyRows = this.csvOnlyRows;
        base.matchedDetailRows = this.matchedDetailRows;
        base.spendingDetailRows = this.spendingDetailRows;
        base.deptVariances = this.deptVariances;
        base.designationVariances = this.designationVariances;
        base.jobGroupVariances = this.jobGroupVariances;
        base.duplicateRows = this.duplicateRows;
        base.inactivePaidRows = this.inactivePaidRows;
      }
      return base;
    },
    async saveReportToServer(title) {
      this.savingReport = true;
      this.saveReportMessage = "";
      this.saveReportError = false;
      try {
        // Slim payload so large months still save (summaries + capped detail)
        let payload = JSON.stringify(this.buildReportPayload({ slim: true }));
        // If still huge, drop detail arrays entirely — keep report totals
        if (payload.length > 400000) {
          const minimal = this.buildReportPayload({ slim: true });
          minimal.matchedDetailRows = [];
          minimal.spendingDetailRows = [];
          minimal.registerOnlyRows = [];
          minimal.csvOnlyRows = [];
          minimal.deptVariances = [];
          minimal.designationVariances = [];
          minimal.jobGroupVariances = [];
          minimal.duplicateRows = [];
          minimal.inactivePaidRows = [];
          if (minimal.pensionRecon) {
            minimal.pensionRecon.variances = (minimal.pensionRecon.variances || []).slice(0, 30);
          }
          if (minimal.gratuityRecon) {
            minimal.gratuityRecon.uploadedRows = [];
          }
          payload = JSON.stringify(minimal);
        }
        const data = new FormData();
        data.append("function", "savePayrollCsvReconciliationReport");
        data.append("title", title);
        data.append("registerScope", this.registerScope);
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("employeeCount", String(this.summary.matchedCount || 0));
        data.append("grossPayroll", String(this.financialSummary?.grossPay || this.summary.grossPay || 0));
        data.append("pensionTotal", String(this.pensionRecon?.totals?.pensionFiles902 || this.financialSummary?.payrollPension902 || 0));
        data.append("gratuityTotal", String(this.gratuityRecon?.uploadedTotal || 0));
        data.append("nitaTotal", String(this.summary.nitaExpected || this.financialSummary?.nitaExpected || 0));
        data.append("varianceTotal", String(this.executiveSummary?.totalVariance || 0));
        data.append("payload", payload);
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.saveReportMessage = res.data.message || "Report saved.";
          this.saveReportError = false;
          await this.saveWageBillSnapshot();
          await this.loadSavedReports();
          await this.loadMultiMonthWageBill();
          this.activeTab = "saved";
        } else {
          this.saveReportMessage = res.data?.message || "Could not save report.";
          this.saveReportError = true;
        }
      } catch (e) {
        this.saveReportMessage = e.message || "Could not save report.";
        this.saveReportError = true;
      } finally {
        this.savingReport = false;
      }
    },
    async loadSavedReport(id) {
      this.errorMsg = "";
      try {
        const data = new FormData();
        data.append("function", "getPayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load report");
        }
        const rep = res.data.data?.report;
        const p = rep?.payload;
        if (!p || typeof p !== "object") {
          throw new Error("Invalid saved data");
        }
        this.applyReportPayload(p);
        if (rep.register_scope) this.registerScope = rep.register_scope;
        this.analysisDone = true;
        this.activeTab = "summary";
        this.saveReportMessage = `Loaded: ${rep.title || "Report #" + id}`;
        this.saveReportError = false;
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    applyReportPayload(p) {
      this.summary = { ...(p.summary || {}) };
      this.financialSummary = p.financialSummary || null;
      this.executiveSummary = p.executiveSummary || null;
      this.pensionRecon = p.pensionRecon || null;
      this.gratuityRecon = p.gratuityRecon || null;
      this.deptVariances = Array.isArray(p.deptVariances) ? p.deptVariances : [];
      this.designationVariances = Array.isArray(p.designationVariances) ? p.designationVariances : [];
      this.jobGroupVariances = Array.isArray(p.jobGroupVariances) ? p.jobGroupVariances : [];
      this.duplicateRows = Array.isArray(p.duplicateRows) ? p.duplicateRows : [];
      this.inactivePaidRows = Array.isArray(p.inactivePaidRows) ? p.inactivePaidRows : [];
      const srows = Array.isArray(p.spendingSummaryRows) ? p.spendingSummaryRows : [];
      this.spendingSummaryRows = srows.map((r, i) => ({
        ...r,
        key: r.key != null ? r.key : `legacy-${r.seq ?? i}-${r.entity ?? ""}`
      }));
      this.wageBillRows = Array.isArray(p.wageBillRows) ? p.wageBillRows : [];
      this.registerOnlyRows = Array.isArray(p.registerOnlyRows) ? p.registerOnlyRows : [];
      this.csvOnlyRows = Array.isArray(p.csvOnlyRows) ? p.csvOnlyRows : [];
      this.matchedDetailRows = Array.isArray(p.matchedDetailRows) ? p.matchedDetailRows : [];
      this.spendingDetailRows = Array.isArray(p.spendingDetailRows) ? p.spendingDetailRows : [];
      const cm = p.columnMaps || {};
      this.mapId = cm.mapId ?? "";
      this.mapDept = cm.mapDept ?? "";
      this.mapPay = cm.mapPay ?? "";
      this.mapName = cm.mapName ?? "";
      if (p.periodYear) this.periodYear = p.periodYear;
      if (p.periodMonth) this.periodMonth = p.periodMonth;
      if (p.nitaPerEmployee != null) this.nitaPerEmployee = p.nitaPerEmployee;
      this.headers = Array.isArray(p.headers) ? [...p.headers] : [];
      this.csvRows = [];
    },
    async deleteUpload(fileType) {
      if (!window.confirm(`Delete the uploaded ${fileType.replace('_', ' ')} file for this month?`)) return;
      try {
        const data = new FormData();
        data.append("function", "deletePayrollReconUpload");
        data.append("periodYear", String(this.periodYear));
        data.append("periodMonth", String(this.periodMonth));
        data.append("fileType", fileType);
        
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          if (this.$toast) this.$toast.success("File deleted successfully");
          this.stagedFiles[fileType] = null;
          this.$el.querySelectorAll('input[type="file"]').forEach(i => i.value = '');
          await this.loadPeriodStatus();
        } else {
          const msg = res.data?.message || "Delete failed";
          if (this.$toast) this.$toast.error(msg);
          else alert(msg);
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || String(e);
        if (this.$toast) this.$toast.error(msg);
        else alert(msg);
      }
    },
    async deleteSavedReport(id) {
      if (!window.confirm("Delete this saved report?")) return;
      try {
        const data = new FormData();
        data.append("function", "deletePayrollCsvReconciliationReport");
        data.append("id", String(id));
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          await this.loadSavedReports();
        } else {
          this.errorMsg = res.data?.message || "Delete failed";
        }
      } catch (e) {
        this.errorMsg = e.message || String(e);
      }
    },
    autoMapColumns() {
      const h = this.headers;
      this.mapId =
        pickColumn(h, [
          "payrollnum",
          "payroll num",
          "idnum",
          "id num",
          "national id",
          "nationalid",
          "pf no",
          "p/f no",
          "pfno",
          "emp no",
          "employee no",
          "staff no",
          "personal file",
          "service number",
          "id no",
          "staff id",
          "employee id",
          "empno",
          "pf number",
          "taxpin",
          "tax pin"
        ]) || "";
      this.mapDept =
        pickColumn(h, [
          "wstationname",
          "work station",
          "station",
          "department",
          "dept",
          "directorate",
          "unit",
          "wcountyname",
          "county"
        ]) || "";
      this.mapPay =
        pickColumn(h, [
          "grosspay",
          "gross pay",
          "gross salary",
          "gross",
          "net pay",
          "net salary",
          "netpay",
          "net",
          "basic pay",
          "basic salary",
          "basic",
          "total pay",
          "amount",
          "pay"
        ]) || "";
      this.mapName =
        pickColumn(h, ["names", "employee name", "staff name", "full name", "name", "surname"]) || "";
    },
    resolvedMaps() {
      const h = this.headers;
      const idCol =
        this.mapId ||
        pickColumn(h, ["payrollnum", "idnum", "pf", "emp", "staff", "employee", "national"]);
      const deptCol =
        this.mapDept ||
        pickColumn(h, ["department", "dept", "wstation", "station", "work station", "wstationname"]);
      let payCol = this.mapPay;
      if (!payCol) {
        payCol =
          pickColumn(h, ["grosspay", "gross"]) ||
          pickColumn(h, ["net"]) ||
          pickColumn(h, ["basic"]) ||
          pickColumn(h, ["amount", "pay", "salary"]);
      }
      const nameCol = this.mapName || pickColumn(h, ["names", "name"]);
      return { idCol, deptCol, payCol, nameCol };
    },

    enrichSpendingSummaryFromLedger(baseRows, pensionRecon) {
      const cols = resolvePayrollCostColumns(this.headers);
      const idCol = this.mapId || cols.id;
      const nitaRate = Number(this.nitaPerEmployee) || 0;
      const byEntity = new Map();

      const ensureEntity = (entity) => {
        const key = String(entity || "(No department)").trim().toLowerCase();
        if (!byEntity.has(key)) {
          byEntity.set(key, {
            entity: String(entity || "(No department)").trim(),
            vote: "",
            voteCode: "",
            staffCount: 0,
            gross: 0,
            net: 0,
            deductions: 0,
            pension902: 0,
            nita: 0,
            basic: 0,
            rental: 0,
            commuter: 0,
            nssf: 0,
            housing: 0,
            pensionEmployer: 0,
            otherAllow: 0,
            total: 0,
            key
          });
        }
        return byEntity.get(key);
      };

      // Staff counts from analysis match (register mapping)
      (baseRows || []).forEach((r) => {
        const agg = ensureEntity(r.entity);
        agg.staffCount = Number(r.staffCount) || 0;
        agg.pension902 = Number(r.pension902) || 0;
        if (!cols.gross) agg.gross = Number(r.gross) || 0;
        // Vote from register departments (already set if provided on base row)
        if (r.vote || r.voteCode) {
          agg.vote = r.vote || r.voteCode;
          agg.voteCode = agg.vote;
        }
      });

      // Amounts strictly from ledger columns (Homabay codes / headers)
      // VOTE always from employee register department.code — never payroll VOTE column
      for (const row of this.csvRows) {
        const rawId = idCol ? row[idCol] : null;
        const emp = rawId && this.registerIndex ? this.registerIndex.get(normId(rawId)) : null;
        if (!emp) continue;
        const entity = resolveSpendingEntity(emp, "", this.deptNameById);
        const agg = ensureEntity(entity);
        const regVote = resolveRegisterVote(emp, this.deptVoteById);
        if (regVote) {
          agg.vote = regVote;
          agg.voteCode = regVote;
        }
        if (cols.basic) agg.basic += parseMoney(row[cols.basic]);
        if (cols.rental) agg.rental += parseMoney(row[cols.rental]);
        if (cols.commuter) agg.commuter += parseMoney(row[cols.commuter]);
        if (cols.gross) agg.gross += parseMoney(row[cols.gross]);
        if (cols.nssf) agg.nssf += parseMoney(row[cols.nssf]);
        if (cols.housing) agg.housing += parseMoney(row[cols.housing]);
        if (cols.nita) agg.nita += parseMoney(row[cols.nita]);
        else if (nitaRate) agg.nita += nitaRate;
      }

      if (pensionRecon) {
        pensionRecon.matched.forEach((r) => {
          const entity = String(r.department || "(No department)").trim();
          const agg = ensureEntity(entity);
          agg.pensionEmployer += Number(r.pensionFile) || 0;
        });
        pensionRecon.missingInPayroll.forEach((r) => {
          const emp = this.registerIndex ? this.registerIndex.get(r.key) : null;
          const entity = resolveSpendingEntity(emp, "", this.deptNameById);
          const agg = ensureEntity(entity);
          agg.pensionEmployer += Number(r.pensionFile) || 0;
        });
      }

      const finalOut = Array.from(byEntity.values());
      finalOut.forEach((r) => {
        if (!Number(r.nita) && nitaRate && r.staffCount) r.nita = r.staffCount * nitaRate;
        r.otherAllow = Math.max(
          0,
          (Number(r.gross) || 0) - (Number(r.basic) || 0) - (Number(r.rental) || 0) - (Number(r.commuter) || 0)
        );
        r.total =
          (Number(r.gross) || 0) +
          (Number(r.nssf) || 0) +
          (Number(r.housing) || 0) +
          (Number(r.pensionEmployer) || 0) +
          (Number(r.nita) || 0);
        r.key = String(r.entity).toLowerCase();
      });
      finalOut.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      finalOut.forEach((r, i) => {
        r.seq = i + 1;
      });
      return finalOut;
    },
    computeSpendingDetailRows(registerIndex, pensionRecon) {
      if (!this.csvRows || !this.csvRows.length) return [];
      const cols = resolvePayrollCostColumns(this.headers);
      const idCol = this.mapId || cols.id;
      const nameCol = this.mapName || cols.name;
      const h = this.headers;
      const payrollNoCol = pickColumn(h, ["payrollnum", "payroll num", "pf no", "p/f no"]);
      const nationalIdCol = pickColumn(h, ["national id", "id no", "idnum", "nationalid"]);
      const nitaRate = Number(this.nitaPerEmployee) || 0;
      const idx = registerIndex || this.registerIndex;
      
      const pensionMap = new Map();
      if (pensionRecon) {
        pensionRecon.matched.forEach(r => pensionMap.set(r.key, Number(r.pensionFile) || 0));
        pensionRecon.missingInPayroll.forEach(r => pensionMap.set(r.key, Number(r.pensionFile) || 0));
      }

      const rows = this.csvRows.map((row) => {
        const rawId = idCol ? row[idCol] : null;
        const key = normId(rawId);
        const emp = rawId && idx ? idx.get(key) : null;
        const entity = resolveSpendingEntity(emp, "", this.deptNameById);
        const basic = cols.basic ? parseMoney(row[cols.basic]) : 0;
        const rental = cols.rental ? parseMoney(row[cols.rental]) : 0;
        const commuter = cols.commuter ? parseMoney(row[cols.commuter]) : 0;
        const gross = cols.gross ? parseMoney(row[cols.gross]) : 0;
        const nssf = cols.nssf ? parseMoney(row[cols.nssf]) : 0;
        const housing = cols.housing ? parseMoney(row[cols.housing]) : 0;
        const pensionEmployer = pensionMap.get(key) || 0;
        const nita = cols.nita ? parseMoney(row[cols.nita]) : emp ? nitaRate : 0;
        return {
          entity,
          staffId: rawId || "",
          payrollNo: payrollNoCol ? row[payrollNoCol] || "" : "",
          nationalId: nationalIdCol ? row[nationalIdCol] || "" : "",
          name: nameCol ? row[nameCol] || "" : "",
          basic,
          rental,
          commuter,
          gross,
          nssf,
          housing,
          pensionEmployer,
          nita,
          otherAllow: Math.max(0, gross - basic - rental - commuter),
          projectedIncrement: basic * 0.04,
          total: gross + nssf + housing + pensionEmployer + nita
        };
      });
      rows.sort((a, b) => String(a.entity).localeCompare(String(b.entity)));
      return rows;
    },
    downloadSampleCsv() {
      const lines = [
        "P/F No,Employee Name,Department,Basic Pay,Gross Pay,Net Pay",
        "PF001,Jane Doe,Finance,50000,75000,65000",
        "PF002,John Smith,Health,80000,120000,95000"
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "sample_payroll_reconciliation.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    },
    async fetchAllEmployees() {
      const all = [];
      let page = 1;
      const rows_per_page = 5000;
      let totalItems = null;
      /* eslint-disable no-await-in-loop */
      while (true) {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", String(page));
        data.append("rows_per_page", String(rows_per_page));
        // County-wide register so spending entities = all departments
        data.append("forPayrollReconciliation", "1");
        data.append("payrollScope", "county");
        const res = await execute(data, authUrl);
        if (!res.data || !res.data.success) {
          throw new Error(res.data?.message || "Failed to load employees");
        }
        const payload = res.data.data || {};
        const batch = Array.isArray(payload.employees) ? payload.employees : [];
        all.push(...batch);
        totalItems = payload.totalItems != null ? Number(payload.totalItems) : all.length;
        if (batch.length === 0 || all.length >= totalItems) break;
        page += 1;
        if (page > 500) break;
      }
      return all;
    },
    filterRegister(list) {
      if (this.registerScope === "all") return list;
      return list.filter((e) => {
        const s = (e.status || "").toString().toLowerCase();
        return s === "active" || s === "";
      });
    },
    async runAnalysis() {
      this.errorMsg = "";
      if (!this.csvRows.length) {
        this.errorMsg = "Choose a payroll file (Excel or CSV) first.";
        return;
      }
      const gratuityCheck = validateGratuityFileForAnalysis(this.gratuityRows, this.gratuityHeaders);
      if (!gratuityCheck.ok) {
        this.errorMsg = gratuityCheck.message;
        window.alert(gratuityCheck.message);
        return;
      }
      await this.loadPeriodStatus();
      if (this.periodFinalized) {
        this.errorMsg = "This month is finalized. Super Admin must reopen before analysis.";
        return;
      }
      if (this.canManage && this.nitaPerEmployee > 0 && (!this.preValidation || !this.preValidation.checks?.nita)) {
        await this.saveNitaConfig();
      }
      const valData = new FormData();
      valData.append("function", "validatePayrollReconPreAnalysis");
      valData.append("periodYear", String(this.periodYear));
      valData.append("periodMonth", String(this.periodMonth));
      const valRes = await execute(valData, authUrl);
      const validation = valRes.data?.data?.validation;
      this.preValidation = validation || this.preValidation;
      if (!validation || !validation.ready) {
        this.errorMsg = (validation?.errors || ["Pre-analysis requirements not met."]).join(" ");
        return;
      }

      const { idCol, payCol } = this.resolvedMaps();
      if (!idCol) {
        this.errorMsg = "Could not detect a staff / employee ID column.";
        return;
      }
      if (!payCol) {
        this.errorMsg = "Could not detect a pay column.";
        return;
      }

      this.loading = true;
      try {
        await this.ensureDeptNameMap();
        const raw = await this.fetchAllEmployees();
        const register = this.filterRegister(raw);
        this.registerIndex = buildRegisterIndex(register);

        const analysis = runRegisterPayrollAnalysis({
          csvRows: this.csvRows,
          headers: this.headers,
          registerList: register,
          columnMaps: {
            mapId: this.mapId,
            mapDept: this.mapDept,
            mapPay: this.mapPay,
            mapName: this.mapName
          },
          nitaPerEmployee: this.nitaPerEmployee,
          deptNameById: this.deptNameById
        });

        this.summary = analysis.summary;
        this.financialSummary = analysis.financialSummary;
        this.wageBillRows = analysis.wageBillRows;
        this.registerOnlyRows = analysis.registerOnlyRows;
        this.csvOnlyRows = analysis.csvOnlyRows;
        this.matchedDetailRows = analysis.matchedDetailRows;
        this.deptVariances = analysis.deptVariances;
        this.designationVariances = analysis.designationVariances;
        this.jobGroupVariances = analysis.jobGroupVariances;
        this.duplicateRows = analysis.duplicateRows;
        this.inactivePaidRows = analysis.inactivePaidRows;
        this.detectedEarnings = analysis.earnings;
        this.detectedDeductions = analysis.deductions;

        this.pensionRecon = reconcilePension({
          payrollMatchedRows: analysis.matchedDetailRows,
          pensionFileRowsList: this.pensionRows,
          pensionMapsList: null
        });

        this.spendingSummaryRows = this.enrichSpendingSummaryFromLedger(analysis.spendingSummaryRows, this.pensionRecon);
        this.spendingDetailRows = this.computeSpendingDetailRows(this.registerIndex, this.pensionRecon);

        this.gratuityRecon = reconcileGratuity({
          gratuityRows: this.gratuityRows,
          headers: this.gratuityHeaders,
          registerList: register,
          payrollMatchedRows: analysis.matchedDetailRows
        });
        this.executiveSummary = buildExecutiveSummary({
          financialSummary: analysis.financialSummary,
          pensionRecon: this.pensionRecon,
          gratuityRecon: this.gratuityRecon,
          spendingSummaryRows: this.spendingSummaryRows
        });

        await this.saveTemplateCatalog(analysis.codesForCatalog);
        // Persist multi-month wage bill snapshot (lightweight) for this month
        await this.saveWageBillSnapshot();

        this.analysisDone = true;
        this.activeTab = "spending";
      } catch (e) {
        this.errorMsg = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    async exportPdfSummary() {
      await this.exportReportPdf("spending");
    },
    async exportReportPdf(kind) {
      this.errorMsg = "";
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const period = this.periodLabel;
        const fm = (n) => formatMoneyPdf(n);
        const lh = this.letterhead;

        if (kind === "spending" || kind === "executive") {
          exportSpendingEntityCostingsPdf({
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            rows: this.spendingSummaryRows || [],
            totals: this.spendingTotals,
            bottom: this.wageBillBottomSummary,
            filename: `spending_entity_costings_${this.periodYear}_${this.periodMonth}.pdf`
          });
          return;
        }

        if (kind === "summary") {
          const rows = [
            ["CSV rows", this.summary.csvRowCount],
            ["Register employees (scope)", this.summary.registerCount],
            ["Matched (CSV ↔ register)", this.summary.matchedCount],
            ["Extra on payroll (CSV only)", this.summary.csvOnlyCount],
            ["Missing from payroll (register only)", this.summary.registerOnlyCount],
            ["Duplicate payroll IDs", this.summary.duplicateCount || 0],
            ["Inactive but paid", this.summary.inactivePaidCount || 0],
            ["Gross payroll", fm(this.summary.grossPay || this.summary.totalCsvPay)],
            ["Pension (902 files)", fm(this.executiveSummary?.totalPensionCost)],
            ["Gratuity (uploaded total)", fm(this.gratuityRecon?.uploadedTotal || this.executiveSummary?.totalGratuityCost)],
            ["NITA expected", fm(this.summary.nitaExpected)],
            ["Total variance", fm(this.executiveSummary?.totalVariance)]
          ];
          exportBrandedTablePdf({
            title: "Payroll vs Register — Summary Report",
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            filename: `payroll_summary_${this.periodYear}_${this.periodMonth}.pdf`,
            head: ["Metric", "Value"],
            body: rows
          });
          return;
        }

        if (kind === "spendingDetail") {
          const detailList = this.filteredSpendingDetailRows || [];
          exportBrandedTablePdf({
            title: "Spending Entities Detailed Report",
            subtitle: this.selectedSpendingEntityDetail
              ? `Entity: ${this.selectedSpendingEntityDetail}`
              : "All spending entities — employee-level costing",
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            orientation: "landscape",
            filename: `spending_entities_detailed_${this.periodYear}_${this.periodMonth}.pdf`,
            head: [
              "S.No.",
              "PAYROLL No.",
              "ID No.",
              "NAME",
              "COUNTY SPENDING ENTITY",
              "Basic Pay",
              "Rental House All.",
              "Commuter All.",
              "Other Allowances",
              "GrossPay",
              "Projected Annual Increment (4% Basic)",
              "NSSF",
              "H/Levy",
              "Pension (Employer)",
              "NITA"
            ],
            body: detailList.slice(0, 2000).map((r, i) => [
              i + 1,
              r.payrollNo,
              r.nationalId,
              r.name,
              r.entity,
              fm(r.basic),
              fm(r.rental),
              fm(r.commuter),
              fm(r.otherAllow),
              fm(r.gross),
              fm(r.projectedIncrement),
              fm(r.nssf),
              fm(r.housing),
              fm(r.pensionEmployer),
              fm(r.nita)
            ])
          });
          return;
        }

        if (kind === "registerOnly") {
          exportBrandedTablePdf({
            title: "Missing from Payroll Report",
            subtitle: "In employee register but not found on payroll file",
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            filename: `missing_from_payroll_${this.periodYear}_${this.periodMonth}.pdf`,
            head: ["Emp No", "National ID", "Name", "Department", "Status"],
            body: (this.registerOnlyRows || []).slice(0, 800).map((e) => [
              e.empNo,
              e.nationalId,
              this.fullName(e),
              resolveSpendingEntity(e, "", this.deptNameById),
              e.status
            ])
          });
          return;
        }

        if (kind === "csvOnly") {
          exportBrandedTablePdf({
            title: "Extra from Payroll Report",
            subtitle: "On payroll file but not matched in employee register",
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            filename: `extra_from_payroll_${this.periodYear}_${this.periodMonth}.pdf`,
            head: ["Staff ID (CSV)", "Name", "Department", "Pay"],
            body: (this.csvOnlyRows || []).slice(0, 800).map((r) => [
              r.staffId,
              r.name,
              r.dept,
              fm(r.pay)
            ])
          });
          return;
        }

        if (kind === "wagebill") {
          const btm = this.wageBillBottomSummary;
          exportBrandedTablePdf({
            title: "Wagebill Summary",
            subtitle: "By county spending entity (register department) — docus wagebill summary layout",
            periodLabel: period,
            logoDataUrl,
            letterhead: lh,
            orientation: "landscape",
            filename: `wagebill_summary_${this.periodYear}_${this.periodMonth}.pdf`,
            head: ["S.No.", "COUNTY SPENDING ENTITY", "Staff", "Basic Pay", "Gross Salary", "Total cost"],
            body: [
              ...(this.spendingSummaryRows || []).map((r, i) => [
                r.seq || i + 1,
                r.entity,
                r.staffCount,
                fm(r.basic),
                fm(r.gross),
                fm(r.total)
              ]),
              [
                "",
                "TOTALS",
                this.spendingTotals.staffCount,
                fm(this.spendingTotals.basic),
                fm(this.spendingTotals.gross),
                fm(this.spendingTotals.total)
              ],
              ["", "TOTAL GROSS SALARY (monthly)", "", "", "", fm(btm.monthly.gross)],
              ["", "NSSF", "", "", "", fm(btm.monthly.nssf)],
              ["", "Housing Levy", "", "", "", fm(btm.monthly.housing)],
              ["", "Pension (Employer)", "", "", "", fm(btm.monthly.pensionEmployer)],
              ["", "NITA", "", "", "", fm(btm.monthly.nita)],
              ["", "Gratuity (TOTAL row)", "", "", "", fm(btm.monthly.gratuity)],
              ["", `TOTAL ${period.toUpperCase()} WAGE BILL`, "", "", "", fm(btm.monthly.totalWageBill)],
              ["", "ANNUAL WAGE BILL PROJECTION", "", "", "", fm(btm.annual.totalWageBill)]
            ]
          });
          return;
        }

        if (kind === "wagebillMulti") {
          if (!this.multiMonthWage.entities.length) {
            await this.loadMultiMonthWageBill();
          }
          const cols = this.multiMonthColumns;
          if (!cols.length) {
            this.errorMsg = "No multi-month wage bill data yet. Analyse and save report for each month first.";
            return;
          }
          exportBrandedTablePdf({
            title: "Multi-Month Wage Bill",
            subtitle: `Year ${this.multiMonthWage.year || this.periodYear} (columns added as months are uploaded)`,
            periodLabel: `FY ${this.multiMonthWage.year || this.periodYear}`,
            logoDataUrl,
            letterhead: lh,
            orientation: "landscape",
            filename: `wage_bill_multimonth_${this.multiMonthWage.year || this.periodYear}.pdf`,
            head: ["Spending entity", ...cols.map((c) => `${c.label}`), "YTD total"],
            body: (this.multiMonthWage.entities || []).map((e) => [
              e.entity,
              ...cols.map((c) => fm((e.months && e.months[c.value] && e.months[c.value].total) || 0)),
              fm(this.entityYtd(e))
            ])
          });
        }
      } catch (e) {
        this.errorMsg = e.message || "PDF export failed.";
        console.error(e);
      }
    },
    exportExcelWorkbook() {
      const wb = XLSX.utils.book_new();
      const exec = this.executiveSummary || {};
      const summarySheet = XLSX.utils.aoa_to_sheet([
        ["Payroll vs Register", this.periodLabel],
        ["Matched", this.summary.matchedCount],
        ["Missing from payroll", this.summary.registerOnlyCount],
        ["Extra on payroll", this.summary.csvOnlyCount],
        ["Total payroll cost", exec.totalPayrollCost || this.financialSummary?.grossPay || 0],
        ["Pension (902)", exec.totalPensionCost || 0],
        ["Gratuity", exec.totalGratuityCost || 0],
        ["NITA", exec.totalNitaCost || 0],
        ["Total variance", exec.totalVariance || 0]
      ]);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Executive");
      if (this.spendingSummaryRows.length) {
        const btm = this.wageBillBottomSummary;
        const spendingAoA = [
          ["COUNTY GOVERNMENT OF HOMA BAY"],
          [`HRIS-Ke ${this.periodLabel} DEPARTMENTAL COSTING SUMMARY`],
          [],
          [
            "S.No.",
            "VOTE",
            "COUNTY SPENDING ENTITY",
            "STAFF EARNING",
            "Basic Pay",
            "Rental House All.",
            "Commuter All.",
            "Other Allowances",
            "GROSS SALARY",
            "NSSF",
            "HOUSING LEVY",
            "PENSION (EMPLOYER)",
            "NITA",
            "TOTAL"
          ],
          ...this.spendingSummaryRows.map((r) => [
            r.seq,
            r.vote || r.voteCode || "",
            r.entity,
            r.staffCount,
            r.basic,
            r.rental,
            r.commuter,
            r.otherAllow,
            r.gross,
            r.nssf,
            r.housing,
            r.pensionEmployer || r.pension902 || 0,
            r.nita,
            r.total
          ]),
          [
            "",
            "",
            "TOTALS",
            this.spendingTotals.staffCount,
            this.spendingTotals.basic,
            this.spendingTotals.rental,
            this.spendingTotals.commuter,
            this.spendingTotals.otherAllow,
            this.spendingTotals.gross,
            this.spendingTotals.nssf,
            this.spendingTotals.housing,
            this.spendingTotals.pensionEmployer,
            this.spendingTotals.nita,
            this.spendingTotals.total
          ],
          [],
          [],
          ["TOTAL WAGE BILL:"],
          ["", "", "", "", "", "", "", "", "", "", "MONTHLY", "ANNUAL PROJECTION"],
          ["TOTAL GROSS SALARY", "", "", "", "", "", "", "", "", "", btm.monthly.gross, btm.annual.gross],
          ["EMPLOYER OBLIGATIONS"],
          ["", "NSSF", "", "", "", "", "", "", "", "", btm.monthly.nssf, btm.annual.nssf],
          ["", "HOUSING LEVY", "", "", "", "", "", "", "", "", btm.monthly.housing, btm.annual.housing],
          ["", "PENSION (EMPLOYER)", "", "", "", "", "", "", "", "", btm.monthly.pensionEmployer, btm.annual.pensionEmployer],
          ["", "NITA", "", "", "", "", "", "", "", "", btm.monthly.nita, btm.annual.nita],
          ["", "GRATUITY", "", "", "", "", "", "", "", "", btm.monthly.gratuity, btm.annual.gratuity],
          ["TOTAL EMPLOYER OBLIGATIONS", "", "", "", "", "", "", "", "", "", btm.monthly.employerObligations, btm.annual.employerObligations],
          [
            `TOTAL ${this.periodLabel.toUpperCase()} WAGE BILL`,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            btm.monthly.totalWageBill,
            btm.annual.totalWageBill
          ]
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(spendingAoA), "Spending");
      }
      if (this.registerOnlyRows.length) {
        const aoa = [
          ["Emp No", "National ID", "Name", "Department", "Status"],
          ...this.registerOnlyRows.slice(0, 5000).map((e) => [
            e.empNo,
            e.nationalId,
            this.fullName(e),
            resolveSpendingEntity(e, "", this.deptNameById),
            e.status
          ])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Missing");
      }
      if (this.csvOnlyRows.length) {
        const aoa = [
          ["Staff ID", "Name", "Department", "Pay"],
          ...this.csvOnlyRows.slice(0, 5000).map((r) => [r.staffId, r.name, r.dept, r.pay])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Extra");
      }
      if (this.pensionRecon && Array.isArray(this.pensionRecon.variances)) {
        const penAoA = [
          ["Emp No", "Name", "Payroll 902", "Pension file", "Variance"],
          ...this.pensionRecon.variances.slice(0, 5000).map((r) => [
            r.empNo,
            r.name,
            r.payrollPension,
            r.pensionFile,
            r.variance
          ])
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(penAoA), "Pension902");
      }
      XLSX.writeFile(wb, `payroll_recon_${this.periodYear}_${this.periodMonth}.xlsx`);
    },
    clearAll() {
      this.csvRows = [];
      this.headers = [];
      this.analysisDone = false;
      this.errorMsg = "";
      this.saveReportMessage = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      this.mapId = this.mapDept = this.mapPay = this.mapName = "";
      this.spendingSummaryRows = [];
      this.spendingDetailRows = [];
      this.wageBillRows = [];
      this.registerOnlyRows = [];
      this.csvOnlyRows = [];
      this.matchedDetailRows = [];
      this.pensionRows = { '16': [], '32': [], '33': [], '101': [] };
      this.pensionHeaders = { '16': [], '32': [], '33': [], '101': [] };
      this.gratuityRows = [];
      this.gratuityHeaders = [];
      this.uploadStatus = {};
      this.pensionRecon = null;
      this.gratuityRecon = null;
      this.executiveSummary = null;
      this.activeTab = "saved";
    },
    exportSummaryCsv() {
      const lines = [
        ...this.csvLetterheadLines("Summary Report"),
        "Metric,Value",
        `CSV rows,${this.summary.csvRowCount}`,
        `Register employees (scope),${this.summary.registerCount}`,
        `Matched,${this.summary.matchedCount}`,
        `Extra from Payroll,${this.summary.csvOnlyCount}`,
        `Missing from Payroll,${this.summary.registerOnlyCount}`,
        `Total pay (CSV),${this.summary.totalCsvPay}`,
        `Gratuity (TOTAL row),${this.gratuityRecon?.uploadedTotal || 0}`
      ];
      this.downloadBlob(lines.join("\n"), `summary_${this.periodYear}_${this.periodMonth}.csv`);
    },
    csvLetterheadLines(reportTitle) {
      const gen = resolveReportGeneratorMeta();
      const county =
        (this.letterhead && (this.letterhead.companyName || this.letterhead.countyName)) ||
        "COUNTY GOVERNMENT OF HOMA BAY";
      const dept =
        (this.letterhead && this.letterhead.departmentLine) ||
        "Department of Public Service Management & Performance Contracting — HRMIS";
      const contact = [
        this.letterhead?.companyAddress,
        this.letterhead?.companyContact,
        this.letterhead?.companyEmail
      ]
        .filter(Boolean)
        .join(" | ");
      return [
        this.csvEscape(String(county).toUpperCase()),
        this.csvEscape(dept),
        contact ? this.csvEscape(contact) : "",
        this.csvEscape(String(reportTitle).toUpperCase()),
        this.csvEscape(`Period: ${this.periodLabel}`),
        ...reportGeneratorCsvLines(gen).map((l) => this.csvEscape(l)),
        ""
      ].filter((line, idx, arr) => line !== "" || idx === arr.length - 1);
    },
    async ensureLetterheadForCsv() {
      if (!this.letterhead) {
        try {
          await this.loadLetterhead();
        } catch (_) {
          /* optional */
        }
      }
    },
    exportTableCsv(kind) {
      this.ensureLetterheadForCsv();
      if (kind === "spending") {
        const hdr = [
          "S.No.",
          "VOTE",
          "County spending entity",
          "Staff earning",
          "Basic pay",
          "Rental house all.",
          "Commuter all.",
          "Other allowances",
          "Gross salary",
          "NSSF",
          "Housing levy",
          "Pension (employer)",
          "NITA",
          "Total"
        ];
        const body = this.spendingSummaryRows.map((r) =>
          [
            r.seq,
            r.vote || r.voteCode || "",
            this.csvEscape(r.entity),
            r.staffCount,
            r.basic,
            r.rental,
            r.commuter,
            r.otherAllow,
            r.gross,
            r.nssf,
            r.housing,
            r.pensionEmployer,
            r.nita,
            r.total
          ].join(",")
        );
        const t = this.spendingTotals;
        const totalLine = [
          "",
          "",
          this.csvEscape("TOTALS"),
          t.staffCount,
          t.basic,
          t.rental,
          t.commuter,
          t.otherAllow,
          t.gross,
          t.nssf,
          t.housing,
          t.pensionEmployer,
          t.nita,
          t.total
        ].join(",");
        const b = this.wageBillBottomSummary;
        const blank = "";
        const bottom = [
          "",
          `"TOTAL WAGE BILL"`,
          "",
          `"MONTHLY"`,
          `"ANNUAL PROJECTION"`
        ];
        const bottomLines = [
          blank,
          blank,
          bottom.join(","),
          [`"TOTAL GROSS SALARY"`, "", "", b.monthly.gross, b.annual.gross].join(","),
          [`"EMPLOYER OBLIGATIONS"`, "", "", "", ""].join(","),
          [`"NSSF"`, "", "", b.monthly.nssf, b.annual.nssf].join(","),
          [`"HOUSING LEVY"`, "", "", b.monthly.housing, b.annual.housing].join(","),
          [`"PENSION (EMPLOYER)"`, "", "", b.monthly.pensionEmployer, b.annual.pensionEmployer].join(","),
          [`"NITA"`, "", "", b.monthly.nita, b.annual.nita].join(","),
          [`"GRATUITY (TOTAL ROW)"`, "", "", b.monthly.gratuity, b.annual.gratuity].join(","),
          [`"TOTAL EMPLOYER OBLIGATIONS"`, "", "", b.monthly.employerObligations, b.annual.employerObligations].join(","),
          [`"TOTAL ${this.periodLabel.toUpperCase()} WAGE BILL"`, "", "", b.monthly.totalWageBill, b.annual.totalWageBill].join(",")
        ];
        const lines = this.csvLetterheadLines("Spending Entity (Costings) Summary")
          .concat([hdr.join(",")])
          .concat(body)
          .concat(this.spendingSummaryRows.length ? [totalLine] : [])
          .concat(bottomLines);
        this.downloadBlob(lines.join("\n"), `SPENDING_ENTITY_COSTINGS_${this.periodYear}_${this.periodMonth}.csv`);
        return;
      }
      if (kind === "wagebill") {
        const h = ["S.No.", "COUNTY SPENDING ENTITY", "Staff", "Basic Pay", "Gross Salary", "Total cost"];
        const body = (this.spendingSummaryRows || []).map((r, i) =>
          [r.seq || i + 1, this.csvEscape(r.entity), r.staffCount, r.basic, r.gross, r.total].join(",")
        );
        const t = this.spendingTotals;
        const b = this.wageBillBottomSummary;
        const lines = this.csvLetterheadLines("Wagebill Summary")
          .concat([h.join(",")])
          .concat(body)
          .concat([
            ["", this.csvEscape("TOTALS"), t.staffCount, t.basic, t.gross, t.total].join(","),
            "",
            [`"TOTAL GROSS SALARY"`, b.monthly.gross, `"ANNUAL"`, b.annual.gross].join(","),
            [`"NSSF"`, b.monthly.nssf, "", b.annual.nssf].join(","),
            [`"HOUSING LEVY"`, b.monthly.housing, "", b.annual.housing].join(","),
            [`"PENSION (EMPLOYER)"`, b.monthly.pensionEmployer, "", b.annual.pensionEmployer].join(","),
            [`"NITA"`, b.monthly.nita, "", b.annual.nita].join(","),
            [`"GRATUITY (TOTAL ROW)"`, b.monthly.gratuity, "", b.annual.gratuity].join(","),
            [`"TOTAL ${this.periodLabel.toUpperCase()} WAGE BILL"`, b.monthly.totalWageBill, "", b.annual.totalWageBill].join(",")
          ]);
        this.downloadBlob(lines.join("\n"), `WAGEBILL_SUMMARY_${this.periodYear}_${this.periodMonth}.csv`);
        return;
      }
      if (kind === "spendingDetail") {
        const h = ["S. No.", "PAYROLL No.", "ID. No.", "NAME", "COUNTY SPENDING ENTITY", "Basic Pay", "Rental House All.", "Commuter All.", "Other Allowances", "GrossPay", "Projected Annual Increment (4% of Basic Pay)", "NSSF", "H/Levy", "Pension (Employer)", "NITA"];
        const list = this.filteredSpendingDetailRows;
        const lines = this.csvLetterheadLines("Spending Entities Detailed Report")
          .concat([h.join(",")])
          .concat(
            list.map((r, i) =>
              [
                i + 1,
                this.csvEscape(r.payrollNo),
                this.csvEscape(r.nationalId),
                this.csvEscape(r.name),
                this.csvEscape(r.entity),
                r.basic, r.rental, r.commuter, r.otherAllow, r.gross, r.projectedIncrement, r.nssf, r.housing, r.pensionEmployer, r.nita
              ].join(",")
            )
          );
        this.downloadBlob(lines.join("\n"), `SPENDING_ENTITIES_DETAILED_${this.periodYear}_${this.periodMonth}.csv`);
        return;
      }
      if (kind === "registerOnly") {
        const h = ["EmpNo", "NationalId", "Name", "Department", "Status"];
        const lines = this.csvLetterheadLines("Missing from Payroll")
          .concat([h.join(",")])
          .concat(
            this.registerOnlyRows.map((e) =>
              [
                this.csvEscape(e.empNo),
                this.csvEscape(e.nationalId),
                this.csvEscape(this.fullName(e)),
                this.csvEscape(e.departmentName || e.departmentID),
                this.csvEscape(e.status)
              ].join(",")
            )
          );
        this.downloadBlob(lines.join("\n"), `MISSING_FROM_PAYROLL_${this.periodYear}_${this.periodMonth}.csv`);
        return;
      }
      if (kind === "csvOnly") {
        const h = ["StaffId", "Name", "Department", "Pay"];
        const lines = this.csvLetterheadLines("Extra from Payroll")
          .concat([h.join(",")])
          .concat(
            this.csvOnlyRows.map((r) =>
              [this.csvEscape(r.staffId), this.csvEscape(r.name), this.csvEscape(r.dept), r.pay].join(",")
            )
          );
        this.downloadBlob(lines.join("\n"), `EXTRA_FROM_PAYROLL_${this.periodYear}_${this.periodMonth}.csv`);
        return;
      }
      if (kind === "matched") {
        const h = ["EmpNo", "RegisterName", "RegisterDept", "CsvDept", "DeptMismatch", "Pay"];
        const lines = this.csvLetterheadLines("Matched Detail")
          .concat([h.join(",")])
          .concat(
            this.matchedDetailRows.map((r) =>
              [
                this.csvEscape(r.empNo),
                this.csvEscape(r.regName),
                this.csvEscape(r.regDept),
                this.csvEscape(r.csvDept),
                r.deptMismatch ? "Yes" : "",
                r.pay
              ].join(",")
            )
          );
        this.downloadBlob(lines.join("\n"), "payroll_csv_matched.csv");
      }
    },
    csvEscape(v) {
      const s = v == null ? "" : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },
    downloadBlob(text, name) {
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.pcr-content {
  width: 100%;
  max-width: 100%;
}
.filters-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  align-items: end;
}
.filter-group.span-full {
  grid-column: 1 / -1;
}
.filter-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}
.filter-input,
.filter-select {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.table-card {
  background: var(--hr-card-bg, #fff);
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid var(--hr-border, #e5e7eb);
}
.pcr-mapping {
  border-top: 1px solid #eee;
  padding-top: 1rem;
}
.mapping-grid {
  display: grid;
  gap: 0.75rem;
  max-width: 720px;
}
.mapping-row {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
}
.pcr-stat {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}
.pcr-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}
.pcr-stat-label {
  font-size: 0.8rem;
  color: #6c757d;
}
.hr-mb-1 {
  margin-bottom: 0.25rem;
}
.hr-mb-2 {
  margin-bottom: 0.5rem;
}
.hr-mb-3 {
  margin-bottom: 1rem;
}
.hr-mt-1 {
  margin-top: 0.25rem;
}
.hr-mt-3 {
  margin-top: 1rem;
}
.hr-small {
  font-size: 0.875rem;
}
.hr-text-muted {
  color: #6c757d;
}
.hr-p-0 {
  padding: 0;
}
.upload-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}
.upload-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.validation-panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: #f8fafc;
}
.validation-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.validation-list li {
  padding: 0.25rem 0;
  font-size: 0.875rem;
}
.validation-list li.ok::before {
  content: "✓ ";
  color: #198754;
}
.validation-list li.fail::before {
  content: "✗ ";
  color: #dc3545;
}
.pcr-gratuity-blink {
  display: inline-block;
  font-weight: 800;
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #b45309;
  animation: pcr-gratuity-blink-kf 1s step-start infinite;
}
@keyframes pcr-gratuity-blink-kf {
  0%,
  49% {
    opacity: 1;
    color: #b45309;
  }
  50%,
  100% {
    opacity: 0.15;
    color: #dc2626;
  }
}
</style>
